import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useReloadOnRouteEnter } from "../hooks/useReloadOnRouteEnter";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import { textIncludes, textIncludesAny } from "../utils/searchText";
import ModalWhatsApp from "../components/ModalWhatsApp";
import ModalEmail from "../components/ModalEmail";
import CitaLinkTelefono from "../components/cita/CitaLinkTelefono";
import { FLOW_OUTCOMES, taskNeedsM1Modal, taskNeedsM2Modal, taskIsM1Type, taskIsM2Type, isLeadTaskExpired, isWhatsappFlowTask, isWhatsappWaitingTask, hasWhatsappReplyPending, parseTaskMetadata, isPostQualificationTaskCode, isVisitFeedbackTask, getVisitAgendaId, isT4bObjecionesDescarteTask, isSinPropiedadesBuscarTask, isT4b1AutoContactTask } from "../constants/leadFlowOutcomes";
import ModalResultadoTareaAgente, { isAgenteResultTask } from "../components/ModalResultadoTareaAgente";
import FichaResumenPropiedad from "../components/propiedades/FichaResumenPropiedad";
import { resolverPropiedadIdPorReferencia } from "../utils/resolverPropiedadIdPorReferencia";
import { guardarFiltrosTareasSession, leerFiltrosTareasSession } from "../utils/tareasFiltrosStorage";
import {
  FECHA_FILTRO_VALORES,
  filtroFechaRangoMatch,
  abrirPickerFecha,
} from "../utils/filtroFechaRango";
import { useAuth } from "../context/AuthContext";
import { usuarioSoloCarteraPropia } from "../utils/usuarioCarteraScope";
import usePermiso from "../hooks/usePermiso";
import {
  TASK_UI,
  getTaskCategoryKey,
  getTaskFilterCategories,
  formatTaskTitleWithCode,
  formatTareaCardFecha,
  getTaskDisplayCode,
  resolveTaskCodeUpper,
  getTareaContactoEtiqueta,
  getTareaEntradaIso,
  formatTaskActionLabel,
  resolveTareaPropiedadRef,
  resolveTareaPropiedadRefs,
  isTareaManualUsuario,
  isCaptacionTask,
} from "../constants/taskUiColors";
import ModalResultadoCualificacion from "../components/ModalResultadoCualificacion";
import ModalResultadoVisita from "../components/ModalResultadoVisita";
import ModalNuevoLead from "../components/ModalNuevoLead";
import ModalCita from "../components/ModalCita";
import ModalCrearTareaCaptacion from "../components/ModalCrearTareaCaptacion";
import ModalNuevaPrecaptacion from "../components/ModalNuevaPrecaptacion";
import { IconoWhatsAppHistorial, WA_VERDE_OFICIAL } from "../components/historial/IconoWhatsAppHistorial";
import { IconoLlamadaHistorial, LLAMADA_AMARILLO } from "../components/historial/IconoLlamadaHistorial";
import { loadCitaModalFromAgenda, loadSegundaVisitaModalContext, fetchPropiedadPorRef, buildCitaDateTimeISO, addMinutosHora } from "../utils/citaAgenda";
import { useCitaModalOverlayOptional } from "../context/CitaModalOverlayContext";
import {
  getTaskBoardColumnKey,
  isTareaVisibleEnTablero,
  isTcAgendaVisitTask,
} from "../utils/taskSchedule";

// ============================================================
// CONSTANTES
// ============================================================
const TIPOS = {
  contactar:          { label: "Contactar",           emoji: "📞" },
  llamar:             { label: "Llamar",               emoji: "📞" },
  segunda_llamada:    { label: "Segunda llamada",      emoji: "📞" },
  revisar_llamada:    { label: "Revisar llamada",      emoji: "⚠️" },
  revisar_contacto:   { label: "Revisar contacto",     emoji: "🔍" },
  revisar_multimedia: { label: "Fotos/vídeos pendientes", emoji: "📸" },
  enviar_propiedades: { label: "Enviar propiedades",   emoji: "🏠" },
  agendar_visita:     { label: "Agendar visita",       emoji: "📅" },
  seguimiento:        { label: "Seguimiento",          emoji: "🔄" },
  llamada:            { label: "Llamada",              emoji: "☎️" },
  whatsapp:           { label: "WhatsApp",             emoji: "💬" },
  email:              { label: "Email",                emoji: "✉️" },
  visita:             { label: "Visita",               emoji: "🚪" },
  captacion:          { label: "Captación",            emoji: "🏡" },
  negociacion:        { label: "Negociación",          emoji: "🤝" },
  cierre:             { label: "Cierre",               emoji: "✅" },
  contactar_ia:       { label: "Ana — Contactar",      emoji: "🤖" },
  seguimiento_ia:     { label: "Ana — Seguimiento",    emoji: "🤖" },
};

const PRIORIDADES = {
  urgente: { label: "Urgente", color: "#ef4444" },
  alta:    { label: "Alta",    color: "#f97316" },
  normal:  { label: "Normal",  color: "#3b82f6" },
  baja:    { label: "Baja",    color: "#94a3b8" },
};

const CANALES = { whatsapp: "💬", email: "✉️", llamada: "📞", visita: "🚪" };
const PHONE_TASK_TYPES = new Set(["contactar", "contactar_ia", "llamar", "segunda_llamada", "revisar_llamada", "revisar_contacto", "llamada", "seguimiento", "seguimiento_ia", "agendar_visita", "visita"]);
const WHATSAPP_TASK_TYPES = new Set(["contactar", "contactar_ia", "seguimiento", "seguimiento_ia", "enviar_propiedades", "agendar_visita", "visita", "whatsapp"]);
const EMAIL_TASK_TYPES = new Set(["seguimiento", "seguimiento_ia", "enviar_propiedades", "email"]);
const MSG_TAREA_NO_REALIZADA_VENCIMIENTO = "Tarea no realizada y cancelada por vencimiento de plazo";

/** Colores opacos por familia de tarea (fondo de tarjeta). */
const TASK_CATEGORY_STYLE = {
  whatsapp:  { bg: TASK_UI.whatsapp, text: TASK_UI.textOnBright, emojiBg: "rgba(0,0,0,0.12)" },
  llamada:   { bg: TASK_UI.llamada, text: TASK_UI.textOnBright, emojiBg: "transparent" },
  llamada_whatsapp: {
    bg: TASK_UI.dualLlamadaWhatsappBg,
    text: TASK_UI.textOnBright,
    emojiBg: "rgba(0,0,0,0.12)",
    borderColor: "rgba(0,0,0,0.1)",
  },
  cita:      { bg: TASK_UI.cita, text: "#ffffff", emojiBg: "rgba(255,255,255,0.2)" },
  captacion: { bg: "#F97316", text: "#000000", emojiBg: "rgba(0,0,0,0.18)" },
  espera:    { bg: TASK_UI.espera, text: "#ffffff", emojiBg: "rgba(255,255,255,0.2)" },
  sistema:   { bg: "#64748B", text: "#ffffff", emojiBg: "rgba(0,0,0,0.2)" },
};

/** Franjas izquierda/derecha según estado de la tarea. */
const TASK_STATUS_STRIPE = {
  pendiente:  { color: "#F97316", outline: false },
  completada: { color: "#22C55E", outline: true },
  no_hecha:   { color: TASK_UI.noHecha, outline: true },
};

const STRIPE_WIDTH_PX = 5;

function isTaskAtrasadaVisual(tarea = {}) {
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  return getColumna(tarea) === "atrasadas";
}

function getTaskStatusStripeKey(tarea = {}) {
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado === "completada") return "completada";
  if (estado === "cancelada") return "no_hecha";
  if ((estado === "pendiente" || estado === "en_curso") && isTareaPlazoVencido(tarea)) return "no_hecha";
  return "pendiente";
}

function buildStatusStripeBoxShadow(stripe) {
  const w = STRIPE_WIDTH_PX;
  const inner = `inset ${w}px 0 0 0 ${stripe.color}, inset -${w}px 0 0 0 ${stripe.color}`;
  if (!stripe.outline) return inner;
  const o = w + 1;
  return `${inner}, inset ${o}px 0 0 0 #000000, inset -${o}px 0 0 0 #000000`;
}

function getTarjetaTareaVisual(tarea, c, isDark) {
  const atrasada = isTaskAtrasadaVisual(tarea);
  const completada = tarea.estado === "completada";
  const category = TASK_CATEGORY_STYLE[getTaskCategoryKey(tarea)] || TASK_CATEGORY_STYLE.sistema;
  const categoryKey = getTaskCategoryKey(tarea);
  const stripeKey = atrasada ? "no_hecha" : getTaskStatusStripeKey(tarea);
  const stripe = TASK_STATUS_STRIPE[stripeKey] || TASK_STATUS_STRIPE.pendiente;

  return {
    atrasada,
    background: category.bg,
    hoverBg: category.bg,
    textPrimary: category.text,
    textSecondary: category.text,
    textMuted: category.text,
    chipBg: category.text === "#ffffff" ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.1)",
    emojiBg:
      categoryKey === "llamada" || categoryKey === "llamada_whatsapp" || categoryKey === "captacion"
        ? "transparent"
        : isAnaTask(tarea)
          ? "linear-gradient(135deg, #7c3aed, #a855f7)"
          : category.emojiBg,
    categoryKey,
    borderTop: `1px solid ${category.borderColor || category.bg}`,
    borderBottom: `1px solid ${category.borderColor || category.bg}`,
    borderLeft: "none",
    borderRight: "none",
    boxShadow: buildStatusStripeBoxShadow(stripe),
    dotColor: stripe.color,
    opacity: completada ? 0.88 : 1,
  };
}

const RESULTADO_CODIGO_LABELS = {
  no_contesta: "No contesta",
  no_contestada: "No contesta",
  contesta: "Contesta",
  conversacion: "Hay conversación",
  sin_conversacion: "Sin conversación útil",
  sin_transcripcion: "Sin transcripción útil",
  call_failed: "Fallo técnico en la llamada",
  whatsapp_enviado: "WhatsApp enviado",
  email_enviado: "Email enviado",
  cancelada: "Cancelada",
  responde: "Responde al WhatsApp",
  no_responde: "No responde al WhatsApp",
  cita_creada: "Cita creada",
  enviar_propiedad: "Enviar propiedad",
  volver_a_llamar: "Volver a llamar",
  interesado_sin_cita: "Interesado sin cita",
  sin_interes: "Sin interés",
  acepta_cita: "Acepta cita",
  no_acepta: "No acepta cita",
  confirma: "Confirma visita",
  no_confirma: "No confirma visita",
  interesado: "Interesado tras visita",
  no_interesado: "No interesado tras visita",
  reservado: "Reservado / Cierre",
  sigue_proceso: "Sigue en proceso",
  objeciones: "Interés con objeciones",
  descarte: "Descarte tras visita",
};

// Columnas del pipeline temporal
const COLUMNAS = [
  { key: "atrasadas",      label: "Atrasadas",      color: "#ef4444", emoji: "🔴" },
  { key: "hoy",            label: "Hoy",             color: "#f97316", emoji: "🟠" },
  { key: "manana",         label: "Mañana",          color: "#eab308", emoji: "🟡" },
  { key: "esta_semana",    label: "Esta semana",     color: "#22c55e", emoji: "🟢" },
  { key: "proxima_semana", label: "Próxima semana",  color: "#f97316", emoji: "🟠" },
  { key: "siguientes",     label: "Siguientes",      color: "#06b6d4", emoji: "🩵" },
  { key: "sin_fecha",      label: "Sin fecha",       color: "#94a3b8", emoji: "⬜" },
];

/** Permisos granulares (frontend/src/config/permisosTree.js → módulo "tareas"). */
const COLUMNA_NODE_ID = {
  atrasadas: "t_col_atrasadas",
  hoy: "t_col_hoy",
  manana: "t_col_manana",
  esta_semana: "t_col_esta_semana",
  proxima_semana: "t_col_proxima_semana",
  siguientes: "t_col_siguientes",
  sin_fecha: "t_col_sin_fecha",
};
const ESTADO_FILTRO_NODE_ID = {
  pendiente: "t_estado_pendientes",
  en_curso: "t_estado_en_curso",
  completada: "t_estado_completadas",
  cancelada: "t_estado_canceladas",
};
const CATEGORIA_FILTRO_NODE_ID = {
  whatsapp: "t_cat_whatsapp",
  llamada: "t_cat_llamada",
  cita: "t_cat_cita",
  captacion: "t_cat_captacion",
  espera: "t_cat_espera",
  sistema: "t_cat_sistema",
};
const TIPO_FILTRO_NODE_ID = {
  contactar: "t_tipo_contactar",
  llamar: "t_tipo_llamar",
  segunda_llamada: "t_tipo_segunda_llamada",
  revisar_llamada: "t_tipo_revisar_llamada",
  revisar_contacto: "t_tipo_revisar_contacto",
  enviar_propiedades: "t_tipo_enviar_propiedades",
  agendar_visita: "t_tipo_agendar_visita",
  seguimiento: "t_tipo_seguimiento",
  llamada: "t_tipo_llamada",
  whatsapp: "t_tipo_whatsapp",
  email: "t_tipo_email",
  visita: "t_tipo_visita",
  captacion: "t_tipo_captacion",
  negociacion: "t_tipo_negociacion",
  cierre: "t_tipo_cierre",
  contactar_ia: "t_tipo_ana_contactar",
  seguimiento_ia: "t_tipo_ana_seguimiento",
};


const ASIGNADOS_FILTRO = new Set(["todos", "agente", "ana"]);
const ESTADO_FILTRO_IDS = ["pendiente", "en_curso", "completada", "cancelada"];
const ASIGNADO_FILTRO_IDS = ["agente", "ana"];
const CATEGORIA_FILTRO_IDS = ["whatsapp", "llamada", "cita", "captacion", "espera", "sistema"];
const CRITERIO_DBL_MS = 380;

const CATEGORIA_FILTRO_LABELS = {
  whatsapp: "WhatsApp",
  llamada: "Llamada",
  cita: "Cita",
  captacion: "Captación",
  espera: "Espera",
  sistema: "Sistema",
};

const ESTADO_FILTRO_LABELS = {
  pendiente: "Pendientes",
  en_curso: "En curso",
  completada: "Completadas",
  cancelada: "Canceladas",
};

/** Modos exclusivos de filtro por estado (un solo chip activo a la vez). */
const ESTADO_MODOS = {
  activas: {
    apiEstado: "activas",
    dedupeFlow: true,
    excludeOverdue: false,
    chipId: null,
  },
  pendientes: {
    apiEstado: "activas",
    dedupeFlow: true,
    excludeOverdue: true,
    chipId: "pendiente",
  },
  en_curso: {
    apiEstado: "en_curso",
    dedupeFlow: true,
    excludeOverdue: false,
    chipId: "en_curso",
  },
  completadas: {
    apiEstado: "completada",
    dedupeFlow: false,
    excludeOverdue: false,
    chipId: "completada",
  },
  canceladas: {
    apiEstado: "cancelada",
    dedupeFlow: false,
    excludeOverdue: false,
    chipId: "cancelada",
  },
  todas: {
    apiEstado: "todas",
    dedupeFlow: false,
    excludeOverdue: false,
    chipId: "todas",
  },
};

function mapLegacyEstadoInclToModo(incl = []) {
  const ids = incl.filter((x) => ESTADO_FILTRO_IDS.includes(x));
  if (ids.length === 1 && ids[0] === "pendiente") return "pendientes";
  if (ids.length === 1 && ids[0] === "en_curso") return "en_curso";
  if (ids.length === 1 && ids[0] === "completada") return "completadas";
  if (ids.length === 1 && ids[0] === "cancelada") return "canceladas";
  return "activas";
}

function resolveEstadoModoFromUrl(sp, session) {
  const modoUrl = sp.get("estado_modo");
  if (modoUrl && ESTADO_MODOS[modoUrl]) return modoUrl;
  const incl = parseListaUrl(sp, "estado_incl");
  if (incl.length) return mapLegacyEstadoInclToModo(incl);
  if (session?.estadoModo && ESTADO_MODOS[session.estadoModo]) return session.estadoModo;
  return "activas";
}

function formatFechaEnvio(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Madrid",
  });
}

function getWhatsappSentAt(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  const code = String(meta.task_code || "").toUpperCase();
  const keys = [];
  if (code === "T1C") keys.push("t1c_whatsapp_sent_at");
  if (code === "T2E" || code === "T3B") keys.push("t3b_whatsapp_sent_at", "t2e_whatsapp_sent_at");
  if (code === "T3B1") keys.push("t3b1_whatsapp_sent_at");
  for (const k of keys) {
    if (meta[k]) return meta[k];
  }
  return null;
}

function buildTareaEnviadaTooltip(tarea = {}) {
  const quien = isAnaTask(tarea)
    ? "Ana"
    : String(tarea.agente_nombre || "agente").trim().split(/\s+/)[0] || "agente";
  const f = formatFechaEnvio(getWhatsappSentAt(tarea));
  const detalle = f ? `${quien} (${f})` : quien;
  return `Enviada por ${detalle}`;
}

function pasaFiltroChip(categorias, incl, excl) {
  if (!incl?.length && !excl?.length) return true;
  if (excl?.length && categorias.some((c) => excl.includes(c))) return false;
  if (incl?.length) return categorias.some((c) => incl.includes(c));
  return true;
}

function parseListaUrl(sp, key) {
  const raw = sp.get(key);
  if (!raw) return [];
  return raw.split(",").map((x) => x.trim()).filter(Boolean);
}

function TareasFiltroChip({ label, active, excluded, onClick, c, isDark, inGroup = false }) {
  const exBg = isDark ? "#991b1b" : "#dc2626";
  const accent = c.accentBlue || "#3b82f6";
  const chipStyle = {
    padding: "4px 13px",
    borderRadius: 18,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s",
    fontFamily: "inherit",
    userSelect: "none",
  };
  if (inGroup) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          ...chipStyle,
          border: "none",
          background: excluded ? exBg : active ? accent : "transparent",
          color: excluded || active ? "#ffffff" : c.textSecondary,
        }}
      >
        {label}
      </button>
    );
  }
  const border = excluded ? "transparent" : active ? accent : c.border;
  const background = excluded ? exBg : active ? accent : c.bgInput;
  const color = excluded || active ? "#ffffff" : c.textSecondary;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...chipStyle,
        border: `1px solid ${border}`,
        background,
        color,
      }}
    >
      {label}
    </button>
  );
}

function normalizarTelefono(valor) {
  return String(valor || "").trim().replace(/\s+/g, "");
}

function inicialesNombre(nombre) {
  const trozos = String(nombre || "").trim().split(/\s+/).filter(Boolean);
  return (trozos.map((x) => x[0]).join("").slice(0, 2) || "CL").toUpperCase();
}

function IconoTareaCategoria({ categoryKey, tipo, emojiBg }) {
  if (categoryKey === "whatsapp") {
    return (
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 7,
          flexShrink: 0,
          background: WA_VERDE_OFICIAL,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 6px rgba(37,211,102,0.35)",
        }}
        aria-hidden
      >
        <IconoWhatsAppHistorial size={15} />
      </div>
    );
  }

  if (categoryKey === "llamada_whatsapp") {
    return (
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 7,
          flexShrink: 0,
          overflow: "hidden",
          display: "flex",
        }}
        aria-hidden
      >
        <div
          style={{
            width: "50%",
            background: LLAMADA_AMARILLO,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconoLlamadaHistorial size={16} />
        </div>
        <div
          style={{
            width: "50%",
            background: WA_VERDE_OFICIAL,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconoWhatsAppHistorial size={11} />
        </div>
      </div>
    );
  }

  if (categoryKey === "llamada") {
    return (
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 7,
          flexShrink: 0,
          background: LLAMADA_AMARILLO,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 6px rgba(255,214,10,0.45)",
        }}
        aria-hidden
      >
        <IconoLlamadaHistorial size={22} />
      </div>
    );
  }

  if (categoryKey === "captacion") {
    return (
      <div
        style={{
          width: 30,
          height: 30,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
        }}
        aria-hidden
      >
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            border: "2px solid #111827",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#111827"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 10.5 12 3l9 7.5" />
            <path d="M5 9.5V20a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V9.5" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: 30,
        height: 30,
        borderRadius: 7,
        flexShrink: 0,
        background: emojiBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
      }}
    >
      {tipo.emoji}
    </div>
  );
}

function getTaskContactInfo(tarea = {}) {
  const nombre = (tarea.lead_nombre || tarea.cliente_nombre || tarea.captacion_nombre || "").trim() || "Cliente";
  const telefonoLlamada = normalizarTelefono(
    tarea.lead_telefono || tarea.cliente_telefono || tarea.captacion_telefono || tarea.lead_whatsapp || tarea.cliente_whatsapp
  );
  const telefonoWhatsApp = normalizarTelefono(
    tarea.lead_whatsapp || tarea.cliente_whatsapp || tarea.lead_telefono || tarea.cliente_telefono || tarea.captacion_telefono
  );
  const email = String(tarea.lead_email || tarea.cliente_email || tarea.captacion_email || "").trim();
  return {
    nombre,
    telefono: telefonoWhatsApp || telefonoLlamada,
    telefonoLlamada,
    telefonoWhatsApp,
    email,
    iniciales: inicialesNombre(nombre),
    clienteId: tarea.cliente_id || null,
  };
}

/** Dirección + población solo para tareas de captación (vista tabla). */
function direccionCaptacionTarea(tarea = {}) {
  if (!isCaptacionTask(tarea) && !tarea.captacion_id) return "";
  let extras = {};
  const rawExtras = tarea.captacion_datos_extras;
  if (rawExtras && typeof rawExtras === "object") extras = rawExtras;
  else {
    try { extras = JSON.parse(rawExtras || "{}"); } catch { extras = {}; }
  }
  const calle = String(tarea.captacion_calle || extras.nombre_via || extras.calle || "").trim();
  const numero = String(tarea.captacion_numero || extras.numero || "").trim();
  const poblacion = String(
    tarea.captacion_poblacion || extras.poblacion_nombre || extras.poblacion || ""
  ).trim();
  const via = calle ? `${calle}${numero ? ` ${numero}` : ""}` : "";
  if (via && poblacion) return `${via} · ${poblacion}`;
  return via || poblacion || "";
}

/** Paleta estable para chips de contacto en la tabla. */
const CONTACTO_AVATAR_COLORS = [
  "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6", "#f59e0b",
  "#10b981", "#ef4444", "#06b6d4", "#a855f7", "#f97316",
];

function colorAvatarContacto(nombre = "") {
  const s = String(nombre || "").trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CONTACTO_AVATAR_COLORS[h % CONTACTO_AVATAR_COLORS.length];
}

function hexConAlpha(hex, alphaHex = "22") {
  const h = String(hex || "").replace("#", "");
  if (h.length === 6) return `#${h}${alphaHex}`;
  return hex;
}

function estiloCategoriaTabla(tarea) {
  const key = getTaskCategoryKey(tarea) || "sistema";
  const cat = TASK_CATEGORY_STYLE[key] || TASK_CATEGORY_STYLE.sistema;
  const solid = key === "llamada_whatsapp" ? TASK_UI.llamada : cat.bg;
  return {
    key,
    solid,
    text: cat.text,
    softBg: key === "llamada_whatsapp"
      ? "linear-gradient(90deg, #ffDE2128, #25D36628)"
      : hexConAlpha(typeof solid === "string" && solid.startsWith("#") ? solid : "#64748B", "24"),
    border: typeof solid === "string" && solid.startsWith("#") ? solid : "#64748B",
  };
}

function getTaskTitle(tarea = {}) {
  const metadata = parseTaskMetadata(tarea);
  return tarea.titulo || metadata.task_label || null;
}

// El flujo de lead tiene UNA sola tarea principal por lead, pero al avanzar/reasignar puede
// quedar la anterior degradada (es_principal=false) aún en estado 'pendiente'. La API de
// tareas no filtra por es_principal, así que eso provoca tarjetas duplicadas del mismo lead.
// Aquí dejamos una sola tarea de flujo por lead con la misma prioridad básica que el modal:
// principal primero y, si empata, la que vence antes.
function dedupeLeadFlowTasks(rows = []) {
  const best = new Map();
  for (const t of rows) {
    const meta = parseTaskMetadata(t);
    if (!(t.lead_id && (meta.flow_id || meta.task_code))) continue;
    const key = String(t.lead_id);
    const prev = best.get(key);
    if (!prev) { best.set(key, t); continue; }
    if (Boolean(t.es_principal) !== Boolean(prev.es_principal)) {
      best.set(key, t.es_principal ? t : prev);
    } else {
      const tv = new Date(t.fecha_vencimiento || t.created_at || 0).getTime();
      const pv = new Date(prev.fecha_vencimiento || prev.created_at || 0).getTime();
      best.set(key, tv <= pv ? t : prev);
    }
  }
  const emitted = new Set();
  const out = [];
  for (const t of rows) {
    const meta = parseTaskMetadata(t);
    if (!(t.lead_id && (meta.flow_id || meta.task_code))) { out.push(t); continue; }
    const key = String(t.lead_id);
    const chosen = best.get(key);
    if (chosen && String(chosen.id) === String(t.id) && !emitted.has(key)) {
      emitted.add(key);
      out.push(chosen);
    }
  }
  return out;
}

function isAnaTask(tarea = {}) {
  if (!tarea || typeof tarea !== "object") return false;
  const metadata = parseTaskMetadata(tarea);
  return tarea.asignada_a === "ana" || metadata.task_owner === "ana";
}

function getQuickOutcomeOptions(tarea = {}) {
  const metadata = parseTaskMetadata(tarea);
  const code = String(metadata.task_code || "").trim().toUpperCase();
  if (code === "T1" || code === "T1B") return [];
  if (code && FLOW_OUTCOMES[code]) return FLOW_OUTCOMES[code];
  return [];
}

function formatTaskFechaHoraES(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Madrid",
  });
}

function isTareaPlazoVencido(tarea = {}) {
  // TC · Cita: fin de visita ≠ atraso del agente (pasa a T4 Cualificar).
  if (isTcAgendaVisitTask(tarea)) return false;
  if (!tarea?.fecha_vencimiento) return false;
  const due = new Date(tarea.fecha_vencimiento);
  return !Number.isNaN(due.getTime()) && due.getTime() < Date.now();
}

function describeResultadoGestion(tarea = {}) {
  const texto = String(tarea.resultado || "").trim();
  if (texto) {
    return texto.replace(/^Resultado de llamada Ana:\s*/i, "Llamada: ");
  }
  const codigo = String(tarea.resultado_codigo || "").trim().toLowerCase();
  if (codigo && RESULTADO_CODIGO_LABELS[codigo]) return RESULTADO_CODIGO_LABELS[codigo];
  if (codigo) return codigo.replace(/_/g, " ");
  return null;
}

function getTaskEstadoTexto(tarea = {}) {
  const estado = String(tarea.estado || "").trim().toLowerCase();

  if (estado === "completada") {
    const cuando = formatTaskFechaHoraES(tarea.completada_en || tarea.updated_at);
    const detalle = describeResultadoGestion(tarea);
    if (cuando && detalle) return `${cuando} — ${detalle}`;
    if (cuando) return cuando;
    if (detalle) return detalle;
    return "Tarea completada";
  }

  if (estado === "cancelada") {
    const detalle = describeResultadoGestion(tarea);
    if (detalle && !/^cancelada$/i.test(detalle)) return detalle;
    return MSG_TAREA_NO_REALIZADA_VENCIMIENTO;
  }

  if ((estado === "pendiente" || estado === "en_curso") && isTareaPlazoVencido(tarea)) {
    return MSG_TAREA_NO_REALIZADA_VENCIMIENTO;
  }

  if (estado === "pendiente" || estado === "en_curso") {
    return "Pendiente de realizar";
  }

  return "—";
}

function getTaskEstadoStyle(tarea = {}, theme, c) {
  const estado = String(tarea.estado || "").trim().toLowerCase();
  const texto = getTaskEstadoTexto(tarea);
  const esNoRealizada =
    texto === MSG_TAREA_NO_REALIZADA_VENCIMIENTO ||
    estado === "cancelada" ||
    ((estado === "pendiente" || estado === "en_curso") && isTareaPlazoVencido(tarea));

  if (estado === "completada") {
    return {
      bg: "#16a34a11",
      border: "1px solid #16a34a33",
      labelColor: "#16a34a",
      textColor: c.textPrimary,
    };
  }
  if (esNoRealizada) {
    return {
      bg: theme.name === "dark" ? "#ef444418" : "#fef2f2",
      border: `1px solid ${theme.name === "dark" ? "#ef444455" : "#fecaca"}`,
      labelColor: "#ef4444",
      textColor: theme.name === "dark" ? "#fecaca" : "#991b1b",
    };
  }
  return {
    bg: c.bgInput,
    border: `1px solid ${c.border}`,
    labelColor: c.textMuted,
    textColor: c.textSecondary,
  };
}

function shouldAutoCompleteAfterExternalAction(tarea = {}) {
  const metadata = parseTaskMetadata(tarea);
  return !metadata.flow_id;
}

function leerFiltrosDesdeUrl(sp) {
  const session = leerFiltrosTareasSession();
  const asignada = sp.get("asignada");
  let asignadoIncl = parseListaUrl(sp, "asign_incl");
  let asignadoExcl = parseListaUrl(sp, "asign_excl");
  let categoriaIncl = parseListaUrl(sp, "cat_incl");
  let categoriaExcl = parseListaUrl(sp, "cat_excl");

  if (!asignadoIncl.length && !asignadoExcl.length && asignada && ASIGNADOS_FILTRO.has(asignada) && asignada !== "todos") {
    asignadoIncl = [asignada];
  }

  // Preferir usuario_id; agente_id / filtroAgente quedan como legado (sesión/URL antiguas).
  const filtroUsuarioUrl = sp.get("usuario_id") || sp.get("agente_id");
  const filtroUsuario = filtroUsuarioUrl
    || session?.filtroUsuario
    || (session?.filtroAgente && session.filtroAgente !== "todos" ? session.filtroAgente : null)
    || "todos";
  const filtroTipo = sp.get("tipo") || session?.filtroTipo || "todos";
  const filtroFechaRaw = sp.get("fecha") || session?.filtroFecha || "todos";
  const filtroFecha = FECHA_FILTRO_VALORES.includes(filtroFechaRaw) ? filtroFechaRaw : "todos";
  const fechaDesde = sp.get("fecha_desde") || session?.fechaDesde || "";
  const fechaHasta = sp.get("fecha_hasta") || session?.fechaHasta || "";
  const filtroContacto = parseFiltroContacto(sp.get("contacto") || session?.filtroContacto || "todos");
  const busqueda = sp.get("q") || session?.busqueda || "";

  return {
    estadoModo: resolveEstadoModoFromUrl(sp, session),
    asignadoIncl: (asignadoIncl.length ? asignadoIncl : session?.asignadoIncl || []).filter((x) => ASIGNADO_FILTRO_IDS.includes(x)),
    asignadoExcl: (asignadoExcl.length ? asignadoExcl : session?.asignadoExcl || []).filter((x) => ASIGNADO_FILTRO_IDS.includes(x)),
    categoriaIncl: (categoriaIncl.length ? categoriaIncl : session?.categoriaIncl || []).filter((x) => CATEGORIA_FILTRO_IDS.includes(x)),
    categoriaExcl: (categoriaExcl.length ? categoriaExcl : session?.categoriaExcl || []).filter((x) => CATEGORIA_FILTRO_IDS.includes(x)),
    filtroUsuario,
    filtroTipo,
    filtroFecha,
    fechaDesde,
    fechaHasta,
    filtroContacto,
    busqueda,
  };
}

/** Fecha de referencia del filtro: vencimiento (tablero) o alta. */
function fechaReferenciaTarea(tarea) {
  const iso = tarea?.fecha_vencimiento || getTareaEntradaIso(tarea) || tarea?.created_at || null;
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Clave estable lead/cliente/captación para el filtro de contacto. */
function contactoKeyFromTarea(tarea = {}) {
  if (tarea.cliente_id) return `cliente:${tarea.cliente_id}`;
  if (tarea.lead_id) return `lead:${tarea.lead_id}`;
  if (tarea.captacion_id) return `captacion:${tarea.captacion_id}`;
  return null;
}

function contactoLabelFromTarea(tarea = {}) {
  return (
    String(tarea.cliente_nombre || tarea.lead_nombre || tarea.captacion_nombre || "").trim()
    || "Sin nombre"
  );
}

function parseFiltroContacto(raw) {
  const v = String(raw || "").trim();
  if (!v || v === "todos") return "todos";
  if (/^(lead|cliente|captacion):.+/i.test(v)) return v;
  return "todos";
}

function getColumna(tarea) {
  return getTaskBoardColumnKey(tarea, Date.now());
}

function isTareaVencidaActiva(tarea = {}) {
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  return getColumna(tarea) === "atrasadas" || isTareaPlazoVencido(tarea);
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
export default function Tareas() {
  const navigate = useNavigate();
  const location = useLocation();
  const citaOverlay = useCitaModalOverlayOptional();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const { usuario } = useAuth();
  const { puede } = usePermiso();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const soloCartera = usuarioSoloCarteraPropia(usuario);

  const [tareas, setTareas] = useState([]);
  const [usuariosFiltro, setUsuariosFiltro] = useState([]);
  const [cargando, setCargando] = useState(true);
  const filtrosIniciales = leerFiltrosDesdeUrl(searchParams);
  const usuarioScopeInicial = soloCartera && usuario?.id
    ? String(usuario.id)
    : filtrosIniciales.filtroUsuario;
  const [estadoModo, setEstadoModo] = useState(filtrosIniciales.estadoModo);
  const [asignadoIncl, setAsignadoIncl] = useState(filtrosIniciales.asignadoIncl);
  const [asignadoExcl, setAsignadoExcl] = useState(filtrosIniciales.asignadoExcl);
  const [categoriaIncl, setCategoriaIncl] = useState(filtrosIniciales.categoriaIncl);
  const [categoriaExcl, setCategoriaExcl] = useState(filtrosIniciales.categoriaExcl);
  const [filtroUsuario, setFiltroUsuario] = useState(usuarioScopeInicial);
  const [filtroTipo, setFiltroTipo] = useState(filtrosIniciales.filtroTipo);
  const [filtroFecha, setFiltroFecha] = useState(filtrosIniciales.filtroFecha);
  const [fechaDesde, setFechaDesde] = useState(filtrosIniciales.fechaDesde);
  const [fechaHasta, setFechaHasta] = useState(filtrosIniciales.fechaHasta);
  const [filtroContacto, setFiltroContacto] = useState(filtrosIniciales.filtroContacto);
  const [busqueda, setBusqueda] = useState(filtrosIniciales.busqueda);
  const [contactoMenuOpen, setContactoMenuOpen] = useState(false);
  const [contactoBusqueda, setContactoBusqueda] = useState("");
  const [contactoMenuPos, setContactoMenuPos] = useState(null);
  /** Columna «Atrasadas» plegada por defecto (mismo patrón que Ventas/Captaciones en Pipeline) */
  const [atrasadasColExpandida, setAtrasadasColExpandida] = useState(false);
  /** Selección en vista tabla (filtro Fecha activo); acciones masivas TBD. */
  const [tablaSelectedIds, setTablaSelectedIds] = useState(() => new Set());
  const tareasChipDblRef = useRef({ id: null, t: 0, timer: null });
  const fechaDesdeRef = useRef(null);
  const contactoFiltroRef = useRef(null);
  const contactoBusquedaRef = useRef(null);
  const modoTablaFecha = filtroFecha !== "todos";
  const [completando, setCompletando] = useState(null);
  const [tareaDetalle, setTareaDetalle] = useState(null);
  const [tareaEditarManual, setTareaEditarManual] = useState(null);
  const [modalCualificacion, setModalCualificacion] = useState(null);
  const [modalResultadoVisita, setModalResultadoVisita] = useState(null);
  const [modalResultadoAgente, setModalResultadoAgente] = useState(null);
  const [modalWhatsAppTarea, setModalWhatsAppTarea] = useState(null);
  const [modalEmailTarea, setModalEmailTarea] = useState(null);
  const [resultado, setResultado] = useState("");
  // Ficha completa del lead (modal unificado): cualquier tarea con lead_id la abre aquí,
  // en vez de un modal específico por tarea. Trae toda la info y gestiona el flujo solo.
  const [leadModal, setLeadModal] = useState(null);
  const [cargandoLead, setCargandoLead] = useState(false);
  /** Modal propietario (precaptación) al clicar contacto de captación en la tabla. */
  const [captacionModal, setCaptacionModal] = useState(null);
  const [modalPropRef, setModalPropRef] = useState(null);
  const [modalCitaVisita, setModalCitaVisita] = useState(null);
  const [citaEdicionAgendaId, setCitaEdicionAgendaId] = useState(null);
  const [citaEdicionSnapshot, setCitaEdicionSnapshot] = useState(null);
  const [citaEdicionInitial, setCitaEdicionInitial] = useState(null);
  const [citaEdicionProperties, setCitaEdicionProperties] = useState([]);
  // Segunda visita (desde el M2): nueva cita con la misma propiedad → nueva T4.
  const [segundaVisitaTarea, setSegundaVisitaTarea] = useState(null);
  const [segundaVisitaSnapshot, setSegundaVisitaSnapshot] = useState(null);
  const [segundaVisitaProps, setSegundaVisitaProps] = useState([]);
  const [segundaVisitaResultado, setSegundaVisitaResultado] = useState("Segunda visita");

  const cargarSeqRef = useRef(0);
  // Instantánea de la tarea abierta en el ModalDetalle, para re-sincronizarla en vivo
  // cuando el backend avanza el flujo (p. ej. T1 → T1b tras "no contesta") sin que el
  // agente tenga que abrir el Pipeline.
  const tareaDetalleRef = useRef(null);
  // Timers del "burst" de refresco tras una acción que probablemente avance la tarea.
  const refreshBurstRef = useRef([]);
  // Lead cuya llamada bridge puede generar T2A → auto-abrir M1 al detectarlo.
  const pendingM1AfterCallLeadRef = useRef(null);

  useEffect(() => { tareaDetalleRef.current = tareaDetalle; }, [tareaDetalle]);

  useEffect(() => { cargarUsuariosFiltro(); }, []);

  const cargarUsuariosFiltro = async () => {
    try {
      const res = await api.get("/agenda/usuarios");
      const lista = Array.isArray(res.data?.data) ? res.data.data
        : Array.isArray(res.data) ? res.data
        : [];
      setUsuariosFiltro(
        lista
          .filter((u) => u.activo !== false)
          .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"))
      );
    } catch (e) { console.error(e); }
  };

  /** agente_id del usuario seleccionado en el filtro (tareas se filtran por cartera de agente). */
  const agenteIdDeUsuarioFiltro = (usuarioId) => {
    if (!usuarioId || usuarioId === "todos") return null;
    const u = usuariosFiltro.find((x) => String(x.id) === String(usuarioId));
    return u?.agente_id ? String(u.agente_id) : null;
  };

  const cargar = useCallback(async () => {
    const seq = ++cargarSeqRef.current;
    try {
      const modo = ESTADO_MODOS[estadoModo] || ESTADO_MODOS.activas;
      const params = new URLSearchParams();
      params.set("estado", modo.apiEstado);
      const unAsignado = asignadoIncl.length === 1 && !asignadoExcl.length;
      if (unAsignado) params.set("asignada_a", asignadoIncl[0]);
      const agenteId = agenteIdDeUsuarioFiltro(filtroUsuario);
      if (agenteId) params.set("agente_id", agenteId);
      const res = await api.get(`/tareas?${params.toString()}`);
      if (seq !== cargarSeqRef.current) return;
      const rows = res.data.data || [];
      setTareas(modo.dedupeFlow ? dedupeLeadFlowTasks(rows) : rows);
    } catch (err) {
      if (seq !== cargarSeqRef.current) return;
      console.error("Error cargando tareas:", err);
    } finally {
      if (seq === cargarSeqRef.current) setCargando(false);
    }
  }, [estadoModo, asignadoIncl, asignadoExcl, filtroUsuario, usuariosFiltro]);

  const cerrarModalCitaVisita = useCallback(() => {
    setModalCitaVisita(null);
    setCitaEdicionAgendaId(null);
    setCitaEdicionSnapshot(null);
    setCitaEdicionInitial(null);
    setCitaEdicionProperties([]);
  }, []);

  const abrirModalCitaVisita = useCallback(async (tarea) => {
    if (!puede("t_modal_cita", "ver")) {
      await dialogs.alert("No tienes permiso para ver la cita de esta tarea.");
      return;
    }
    const agendaId = getVisitAgendaId(tarea);
    if (!agendaId) {
      await dialogs.alert("Esta tarea no tiene una cita vinculada en agenda.");
      return;
    }
    try {
      const { cita, propiedadesModal, initialValues } = await loadCitaModalFromAgenda(api, agendaId, {
        mergeLeadRefs: false,
      });
      setModalCitaVisita(tarea);
      setCitaEdicionAgendaId(String(cita.id));
      setCitaEdicionSnapshot(cita);
      setCitaEdicionInitial(initialValues);
      setCitaEdicionProperties(propiedadesModal);
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || "No se pudo abrir la cita.");
    }
  }, [puede]);

  const abrirModalResultadoVisita = useCallback(async (tarea) => {
    let tareaFresh = tarea;
    if (tarea?.lead_id) {
      try {
        await api.post(`/tareas/lead/${tarea.lead_id}/reabrir-modal`, { tarea_id: tarea.id });
        const r = await api.get(`/tareas/lead/${tarea.lead_id}/activa`);
        if (r.data?.tarea) tareaFresh = r.data.tarea;
      } catch { /* usa tarea original */ }
    }
    setModalResultadoVisita(tareaFresh);
  }, []);

  /** Ficha cliente + modal de tarea (M1/M2), como T3b Evaluar respuesta. */
  const abrirFichaConModalTarea = useCallback(async (tarea) => {
    let tareaFresh = tarea;
    if (tarea?.lead_id) {
      try {
        await api.post(`/tareas/lead/${tarea.lead_id}/reabrir-modal`, { tarea_id: tarea.id });
        const r = await api.get(`/tareas/lead/${tarea.lead_id}/activa`);
        if (r.data?.tarea) tareaFresh = r.data.tarea;
      } catch { /* usa tarea original */ }
    }
    const clienteId = tareaFresh?.cliente_id || tarea?.cliente_id;
    if (!clienteId) return false;
    navigate(`/clientes/${clienteId}`, {
      state: { abrirTareaVigente: true, tareaId: tareaFresh?.id || tarea?.id || null },
    });
    return true;
  }, [navigate]);

  const handleCancelarCitaVisita = useCallback(async () => {
    if (!citaEdicionAgendaId) return;
    if (!(await dialogs.confirm(
      "¿Cancelar esta cita?\n\nSe marcará como cancelada, se cerrará la tarea de la visita y el lead volverá a Atendidos."
    ))) return;
    try {
      const { data } = await api.post(`/agenda/${citaEdicionAgendaId}/cancelar`);
      if (data?.ok === false) {
        await dialogs.alert(data?.error || "No se pudo cancelar la cita.");
        return;
      }
      cerrarModalCitaVisita();
      await cargar();
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || "No se pudo cancelar la cita.");
    }
  }, [citaEdicionAgendaId, cerrarModalCitaVisita, cargar]);

  const handleCitaVisitaConfirm = useCallback(async (data, options = {}) => {
    try {
      const propsVisita = data?.propiedades || [];
      const agendaId = options.agendaId ?? citaEdicionAgendaId;
      if (!agendaId) {
        throw new Error("No se pudo identificar la cita a editar.");
      }
      const prev = options.snapshot ?? citaEdicionSnapshot ?? {};
      const tareaCtx = options.tarea ?? modalCitaVisita;
      const fechaInicio = buildCitaDateTimeISO(data.fecha, data.horaInicio);
      const bloques = Math.max(1, propsVisita.length);
      const horaFin = data.horaFin || addMinutosHora(data.horaInicio, bloques * (data.duracion || 45));
      const fechaFin = buildCitaDateTimeISO(data.fecha, horaFin);
      const propiedadIds = propsVisita.map((p) => p.id).filter(Boolean);
      const lineasHorario = propsVisita.map((p) => {
        const ref = p.referencia || p.id;
        return `${p.horaVisita || data.horaInicio} · Ref. ${ref}`;
      });
      await api.put(`/agenda/${agendaId}`, {
        id: agendaId,
        agente_id: prev.agente_id ? String(prev.agente_id) : null,
        usuario_id: prev.usuario_id ? String(prev.usuario_id) : null,
        lead_id: prev.lead_id || tareaCtx?.lead_id || null,
        cliente_id: prev.cliente_id || tareaCtx?.cliente_id || null,
        contacto_id: prev.contacto_id || null,
        captacion_id: prev.captacion_id || null,
        titulo: prev.titulo || "Visita",
        descripcion: propsVisita.length
          ? `Visita programada.\n${lineasHorario.join("\n")}`
          : (prev.descripcion || "Visita programada."),
        tipo: prev.tipo || "visita",
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        estado: prev.estado || "pendiente",
        direccion: prev.direccion || null,
        notas: prev.notas || null,
        propiedad_ids: propiedadIds.length ? propiedadIds : null,
        creado_por_ia: prev.creado_por_ia || false,
        confirmado_por_cliente: prev.confirmado_por_cliente || false,
      });
      if (!options.skipClose) cerrarModalCitaVisita();
      await cargar();
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || "No se pudo guardar la cita.");
      throw e;
    }
  }, [citaEdicionAgendaId, citaEdicionSnapshot, modalCitaVisita, cerrarModalCitaVisita, cargar]);

  const cerrarSegundaVisita = useCallback(() => {
    setSegundaVisitaTarea(null);
    setSegundaVisitaSnapshot(null);
    setSegundaVisitaProps([]);
    setSegundaVisitaResultado("Segunda visita");
  }, []);

  const abrirSegundaVisita = useCallback(async (tarea, ctx = {}) => {
    const observaciones = ctx?.observaciones || "";
    const obs = String(observaciones || "").trim();
    setSegundaVisitaResultado(obs ? `Segunda visita — ${obs}` : "Segunda visita");
    const propiedadIds = [
      ...new Set(
        [
          ...(Array.isArray(ctx?.propiedad_ids) ? ctx.propiedad_ids : []),
          ctx?.propiedad_id,
        ]
          .map((id) => String(id || "").trim())
          .filter((id) => id && !id.startsWith("ref-")),
      ),
    ];
    try {
      const { cita, propiedadesModal } = await loadSegundaVisitaModalContext(api, tarea, {
        clienteId: tarea?.cliente_id,
        propiedadIds: propiedadIds.length ? propiedadIds : null,
      });
      setSegundaVisitaTarea(tarea);
      setSegundaVisitaSnapshot({
        ...cita,
        _m2_calificaciones: Array.isArray(ctx?.calificaciones) ? ctx.calificaciones : [],
      });
      setSegundaVisitaProps(propiedadesModal);
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || "No se pudo abrir la cita.");
    }
  }, []);

  const handleSegundaVisitaConfirm = useCallback(async (data) => {
    try {
      const propsVisita = data?.propiedades || [];
      const tarea = segundaVisitaTarea;
      const prev = segundaVisitaSnapshot || {};
      const fechaInicio = buildCitaDateTimeISO(data.fecha, data.horaInicio);
      const bloques = Math.max(1, propsVisita.length);
      const horaFin = data.horaFin || addMinutosHora(data.horaInicio, bloques * (data.duracion || 45));
      const fechaFin = buildCitaDateTimeISO(data.fecha, horaFin);
      const propiedadIds = propsVisita.map((p) => p.id).filter(Boolean);
      const lineasHorario = propsVisita.map((p) => `${p.horaVisita || data.horaInicio} · Ref. ${p.referencia || p.id}`);
      const califs = Array.isArray(prev._m2_calificaciones) ? prev._m2_calificaciones : [];
      await api.post(`/tareas/lead/${tarea.lead_id}/feedback-visita`, {
        accion: "segunda_visita",
        tarea_id: tarea.id,
        resultado: segundaVisitaResultado || "Segunda visita",
        ...(califs.length ? { calificaciones: califs } : {}),
        agendaPayload: {
          agente_id: prev.agente_id ? String(prev.agente_id) : null,
          usuario_id: prev.usuario_id ? String(prev.usuario_id) : null,
          cliente_id: prev.cliente_id || tarea.cliente_id || null,
          propiedad_ids: propiedadIds.length ? propiedadIds : null,
          titulo: prev.titulo || "Visita",
          descripcion: propsVisita.length
            ? `Segunda visita programada.\n${lineasHorario.join("\n")}`
            : "Segunda visita programada.",
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        },
      });
      cerrarSegundaVisita();
      await cargar();
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || "No se pudo crear la segunda visita.");
      throw e;
    }
  }, [segundaVisitaTarea, segundaVisitaSnapshot, segundaVisitaResultado, cerrarSegundaVisita, cargar]);

  // Re-sincroniza el ModalDetalle abierto con la tarea ACTIVA real del lead. Si el backend
  // avanzó el flujo (T1 → T1b tras "no contesta", etc.), actualiza el modal y la tarjeta de
  // la lista sin tener que abrir el Pipeline. Solo aplica a tareas del flujo de leads.
  const resyncModalDetalle = useCallback(async () => {
    const abierta = tareaDetalleRef.current;
    if (!abierta?.lead_id || !parseTaskMetadata(abierta).flow_id) return;
    try {
      const r = await api.get(`/tareas/lead/${abierta.lead_id}/activa`);
      const activa = r.data?.tarea || null;
      if (!activa?.id) return;
      const cambioTarea = String(activa.id) !== String(abierta.id);
      const cambioEstado = String(activa.estado || "") !== String(abierta.estado || "");
      if (!cambioTarea && !cambioEstado) return;
      // Actualiza el modal abierto a la tarea vigente.
      setTareaDetalle((prev) => (prev && String(prev.id) === String(abierta.id) ? activa : prev));
      // Refleja también el cambio en la tarjeta de la lista (sustituye la vieja por la nueva).
      setTareas((prev) => {
        const sinVieja = prev.filter((t) => String(t.id) !== String(abierta.id));
        const yaEsta = sinVieja.some((t) => String(t.id) === String(activa.id));
        return yaEsta
          ? sinVieja.map((t) => (String(t.id) === String(activa.id) ? activa : t))
          : [activa, ...sinVieja];
      });
    } catch { /* silencioso: reintenta en el siguiente ciclo */ }
  }, []);

  const intentarAbrirM1TrasLlamada = useCallback(async (leadId) => {
    if (!leadId) return;
    try {
      const r = await api.get(`/tareas/lead/${leadId}/activa`);
      const tActualizada = r.data?.tarea || null;
      if (taskNeedsM1Modal(tActualizada)) {
        pendingM1AfterCallLeadRef.current = null;
        setModalCualificacion(tActualizada);
      }
    } catch { /* reintenta en el siguiente ciclo del burst */ }
  }, []);

  // Lanza una ráfaga de refrescos tras una acción (llamada/WhatsApp/outcome) que el backend
  // puede convertir en un avance de tarea de forma asíncrona (p. ej. el resultado de la
  // llamada llega unos segundos después). Así la tarjeta pasa a T1b sin esperar al poll lento.
  const lanzarRefrescoBurst = useCallback((leadIdForM1 = null) => {
    if (leadIdForM1) pendingM1AfterCallLeadRef.current = leadIdForM1;
    refreshBurstRef.current.forEach((t) => clearTimeout(t));
    refreshBurstRef.current = [2500, 6000, 11000, 18000, 30000, 45000].map((ms) =>
      setTimeout(async () => {
        await cargar();
        await resyncModalDetalle();
        if (pendingM1AfterCallLeadRef.current) {
          await intentarAbrirM1TrasLlamada(pendingM1AfterCallLeadRef.current);
        }
      }, ms)
    );
  }, [cargar, resyncModalDetalle, intentarAbrirM1TrasLlamada]);

  useEffect(() => () => refreshBurstRef.current.forEach((t) => clearTimeout(t)), []);

  useReloadOnRouteEnter(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    const intervalo = setInterval(() => cargar(), 12000);
    return () => clearInterval(intervalo);
  }, [cargar]);

  // Mientras el ModalDetalle de una tarea del flujo esté abierto, sondea su tarea activa
  // cada 4 s para reflejar al instante el avance del flujo (y cerrar el desfase con el modal).
  useEffect(() => {
    if (!tareaDetalle?.lead_id || !parseTaskMetadata(tareaDetalle).flow_id) return;
    const id = setInterval(resyncModalDetalle, 4000);
    return () => clearInterval(id);
  }, [tareaDetalle, resyncModalDetalle]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (estadoModo && estadoModo !== "activas") p.set("estado_modo", estadoModo);
    if (asignadoIncl.length) p.set("asign_incl", asignadoIncl.join(","));
    if (asignadoExcl.length) p.set("asign_excl", asignadoExcl.join(","));
    if (categoriaIncl.length) p.set("cat_incl", categoriaIncl.join(","));
    if (categoriaExcl.length) p.set("cat_excl", categoriaExcl.join(","));
    if (filtroUsuario !== "todos") p.set("usuario_id", filtroUsuario);
    if (filtroContacto !== "todos") p.set("contacto", filtroContacto);
    if (filtroTipo !== "todos") p.set("tipo", filtroTipo);
    if (filtroFecha !== "todos") p.set("fecha", filtroFecha);
    if (filtroFecha === "custom" && fechaDesde) p.set("fecha_desde", fechaDesde);
    if (filtroFecha === "custom" && fechaHasta) p.set("fecha_hasta", fechaHasta);
    const q = busqueda.trim();
    if (q) p.set("q", q);
    guardarFiltrosTareasSession({
      estadoModo,
      asignadoIncl,
      asignadoExcl,
      categoriaIncl,
      categoriaExcl,
      filtroUsuario,
      filtroContacto,
      filtroTipo,
      filtroFecha,
      fechaDesde: filtroFecha === "custom" ? fechaDesde : "",
      fechaHasta: filtroFecha === "custom" ? fechaHasta : "",
      busqueda: q,
    });
    const next = p.toString();
    if (next !== searchParams.toString()) {
      setSearchParams(p, { replace: true });
    }
  }, [estadoModo, asignadoIncl, asignadoExcl, categoriaIncl, categoriaExcl, filtroUsuario, filtroContacto, filtroTipo, filtroFecha, fechaDesde, fechaHasta, busqueda, searchParams, setSearchParams]);

  useEffect(() => {
    if (soloCartera && usuario?.id) {
      setFiltroUsuario(String(usuario.id));
    }
  }, [soloCartera, usuario?.id]);

  useEffect(() => {
    if (!modoTablaFecha) setTablaSelectedIds(new Set());
  }, [modoTablaFecha]);

  // Normalizar legado: si el valor guardado era agente_id, mapearlo a usuario_id.
  useEffect(() => {
    if (!usuariosFiltro.length || filtroUsuario === "todos") return;
    if (usuariosFiltro.some((u) => String(u.id) === String(filtroUsuario))) return;
    const porAgente = usuariosFiltro.find((u) => u.agente_id && String(u.agente_id) === String(filtroUsuario));
    if (porAgente) setFiltroUsuario(String(porAgente.id));
  }, [usuariosFiltro, filtroUsuario]);

  const scheduleTareasChipInclExcl = (id, onSingle, onDouble) => {
    const now = Date.now();
    const r = tareasChipDblRef.current;
    if (r.id === id && now - r.t < CRITERIO_DBL_MS) {
      if (r.timer) {
        clearTimeout(r.timer);
        r.timer = null;
      }
      r.id = null;
      r.t = 0;
      onDouble();
      return;
    }
    r.id = id;
    r.t = now;
    if (r.timer) clearTimeout(r.timer);
    r.timer = setTimeout(() => {
      r.timer = null;
      r.id = null;
      onSingle();
    }, CRITERIO_DBL_MS);
  };

  const onTareasFiltroChipPress = (grupo, filtroId, isReset = false) => {
    if (grupo === "estado") {
      if (isReset) {
        setEstadoModo("activas");
        return;
      }
      const map = {
        pendiente: "pendientes",
        en_curso: "en_curso",
        completada: "completadas",
        cancelada: "canceladas",
        todas: "todas",
      };
      setEstadoModo(map[filtroId] || "activas");
      return;
    }
    const sets = {
      asignado: [asignadoIncl, setAsignadoIncl, asignadoExcl, setAsignadoExcl, ASIGNADO_FILTRO_IDS],
      categoria: [categoriaIncl, setCategoriaIncl, categoriaExcl, setCategoriaExcl, CATEGORIA_FILTRO_IDS],
    };
    const [incl, setIncl, excl, setExcl, allIds] = sets[grupo] || [];
    if (!setIncl) return;
    scheduleTareasChipInclExcl(
      `${grupo}:${filtroId}`,
      () => {
        if (isReset) {
          setIncl([]);
          setExcl([]);
          return;
        }
        setIncl((inc) => {
          if (inc.includes(filtroId)) return inc.filter((x) => x !== filtroId);
          return [...inc, filtroId];
        });
        setExcl((ex) => ex.filter((x) => x !== filtroId));
      },
      () => {
        if (isReset) {
          setIncl([]);
          setExcl([...allIds]);
          return;
        }
        setExcl((ex) => {
          if (ex.includes(filtroId)) return ex.filter((x) => x !== filtroId);
          return [...ex, filtroId];
        });
        setIncl((inc) => inc.filter((x) => x !== filtroId));
      },
    );
  };

  // Abre la ficha del lead (modal unificado) cargando el lead completo desde la API.
  const abrirLeadModal = useCallback(async (leadId) => {
    if (!leadId) return;
    setCargandoLead(true);
    try {
      const res = await api.get(`/leads/${leadId}`);
      const data = res.data?.data;
      if (data) setLeadModal(data);
      else await dialogs.alert("No se pudo cargar la ficha del lead");
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || "No se pudo cargar la ficha del lead");
    } finally {
      setCargandoLead(false);
    }
  }, []);

  /**
   * Tabla Fecha: contacto clicable.
   * - cliente → ficha (return a Tareas)
   * - captación/propietario → ModalNuevaPrecaptacion encima de la tabla
   * - solo lead → modal lead encima de la tabla
   */
  const abrirContactoDesdeTabla = useCallback(async (e, tarea) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!tarea) return;
    if (tarea.cliente_id) {
      navigate(`/clientes/${tarea.cliente_id}`, {
        state: { returnTo: `${location.pathname}${location.search}` },
      });
      return;
    }
    if (tarea.captacion_id) {
      const seed = {
        id: tarea.captacion_id,
        nombre: tarea.captacion_nombre || "",
        telefono: tarea.captacion_telefono || "",
        email: tarea.captacion_email || "",
        calle: tarea.captacion_calle || "",
        numero: tarea.captacion_numero || "",
        poblacion: tarea.captacion_poblacion || "",
        datos_extras: tarea.captacion_datos_extras || {},
        agente_nombre: tarea.agente_nombre || "",
      };
      setCaptacionModal(seed);
      try {
        const res = await api.get(`/captaciones/${tarea.captacion_id}`);
        const data = res.data?.data || res.data;
        if (data?.id) {
          setCaptacionModal((prev) => (prev?.id === data.id ? { ...seed, ...data } : prev));
        }
      } catch (err) {
        console.warn("[Tareas] cargar captación:", err?.message || err);
        await dialogs.alert(err.response?.data?.error || "No se pudo abrir la ficha del propietario");
        setCaptacionModal(null);
      }
      return;
    }
    if (tarea.lead_id) {
      await abrirLeadModal(tarea.lead_id);
    }
  }, [navigate, location.pathname, location.search, abrirLeadModal]);

  const completar = async (tarea, opciones = {}) => {
    setCompletando(tarea.id);
    try {
      const resultadoFinal = opciones.resultado ?? resultado ?? null;
      const resultadoCodigoFinal = opciones.resultadoCodigo ?? resultadoFinal;
      await api.patch(`/tareas/${tarea.id}`, {
        estado: "completada",
        resultado: resultadoFinal,
        resultado_codigo: resultadoCodigoFinal,
        completada_en: new Date().toISOString(),
      });
      setTareaDetalle(null);
      setModalWhatsAppTarea(null);
      setModalEmailTarea(null);
      setResultado("");
      await cargar();
      if (parseTaskMetadata(tarea).flow_id) lanzarRefrescoBurst();

      const code = String(parseTaskMetadata(tarea).task_code || "").toUpperCase();
      if (isSinPropiedadesBuscarTask(tarea) && opciones.resultadoCodigo === "enviado" && tarea.lead_id) {
        try {
          const r = await api.get(`/tareas/lead/${tarea.lead_id}/activa`);
          const tActualizada = r.data?.tarea || null;
          if (taskNeedsM1Modal(tActualizada)) {
            setModalCualificacion(tActualizada);
          } else if (taskIsM2Type(tActualizada)) {
            await abrirModalResultadoVisita(tActualizada);
          }
        } catch {
          /* ignorar */
        }
      }
      return true;
    } catch (err) {
      console.error(err);
      return false;
    } finally { setCompletando(null); }
  };

  const llamarTelefonoCRM = async (tarea, telefono) => {
    const telefonoNorm = normalizarTelefono(telefono);
    if (!telefonoNorm) {
      await dialogs.alert("Esta tarea no tiene teléfono");
      return;
    }
    try {
      const data = await api.post("/telnyx/llamar", {
        telefonoCliente: telefonoNorm,
        clienteId: tarea?.cliente_id || null,
        leadId: tarea?.lead_id || null,
        vincularCitaPendiente: false,
      }).then((r) => r.data);
      if (data?.ok) {
        // El resultado de la llamada (p. ej. T2L → T2A + M1) llega de forma asíncrona.
        lanzarRefrescoBurst(tarea?.lead_id || null);
        await dialogs.alert("📞 Llamada iniciada — tu móvil recibirá la llamada en breve", { autoCloseMs: 2000 });
      } else {
        await dialogs.alert(`Error: ${data?.error || "No se pudo iniciar la llamada"}`);
      }
    } catch (e) {
      await dialogs.alert(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  const transferCitaVisitaModalAFicha = useCallback((path) => {
    if (!path) return;
    if (!citaOverlay) {
      navigate(path);
      return;
    }
    const snapshot = citaEdicionSnapshot;
    const agendaId = citaEdicionAgendaId;
    const tarea = modalCitaVisita;
    citaOverlay.transferModalToFicha({
      path,
      returnTo: `${location.pathname}${location.search}`,
      onCloseLocal: cerrarModalCitaVisita,
      modalProps: {
        onConfirm: (data) => handleCitaVisitaConfirm(data, { snapshot, agendaId, tarea, skipClose: true }),
        modoEdicion: true,
        readOnly: !puede("t_modal_cita", "modificar"),
        initialValues: citaEdicionInitial,
        contact: getTaskContactInfo(tarea),
        properties: citaEdicionProperties,
        agentName: tarea?.agente_nombre || "",
        onLlamarTelefono: (tel) => llamarTelefonoCRM(tarea, tel),
        onBuscarPropiedadPorRef: (ref) => fetchPropiedadPorRef(api, ref),
        footerExtra: puede("t_modal_cita", "modificar") ? (
          <button
            type="button"
            onClick={async () => {
              if (!(await dialogs.confirm(
                "¿Cancelar esta cita?\n\nSe marcará como cancelada, se cerrará la tarea de la visita y el lead volverá a Atendidos."
              ))) return;
              try {
                const { data: resp } = await api.post(`/agenda/${agendaId}/cancelar`);
                if (resp?.ok === false) {
                  await dialogs.alert(resp?.error || "No se pudo cancelar la cita.");
                  return;
                }
                citaOverlay.clearOverlay({ navigateReturn: true });
              } catch (e) {
                await dialogs.alert(e?.response?.data?.error || e?.message || "No se pudo cancelar la cita.");
              }
            }}
            style={{
              padding: "11px 18px", borderRadius: 10,
              border: "1.5px solid #EF4444", background: "#EF444422", color: "#EF4444",
              cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600,
            }}
          >
            Cancelar cita
          </button>
        ) : null,
      },
    });
  }, [
    citaOverlay,
    navigate,
    location.pathname,
    location.search,
    citaEdicionSnapshot,
    citaEdicionAgendaId,
    modalCitaVisita,
    citaEdicionInitial,
    citaEdicionProperties,
    puede,
    cerrarModalCitaVisita,
    handleCitaVisitaConfirm,
  ]);

  const eliminar = async (tarea) => {
    if (!(await dialogs.confirm(`¿Eliminar la tarea "${tarea.descripcion || tarea.tipo}"? Esta acción no se puede deshacer.`))) return;
    try {
      await api.delete(`/tareas/${tarea.id}`);
      setTareaDetalle(null);
      await cargar();
    } catch (err) { console.error(err); }
  };

  const estadoModoConfig = ESTADO_MODOS[estadoModo] || ESTADO_MODOS.activas;

  /** Filtros de lista excepto lead/contacto (para armar opciones del desplegable). */
  const tareasTrasFiltrosBase = useMemo(() => tareas.filter((t) => {
    if (busqueda.trim()) {
      const tipoLabel = TIPOS[t.tipo]?.label || t.tipo || "";
      const tituloFmt = formatTaskTitleWithCode(t, tipoLabel);
      const codeRaw = resolveTaskCodeUpper(t);
      const codeDisplay = getTaskDisplayCode(t);
      if (!textIncludesAny(
        [
          t.titulo, tituloFmt, t.descripcion, t.lead_nombre, t.cliente_nombre, t.captacion_nombre,
          t.agente_nombre, t.propiedad_ref, t.tipo, t.canal, codeRaw, codeDisplay,
        ],
        busqueda,
      )) return false;
    }
    if (estadoModoConfig.excludeOverdue && isTareaVencidaActiva(t)) return false;
    const asignCat = isAnaTask(t) ? "ana" : "agente";
    if (!pasaFiltroChip([asignCat], asignadoIncl, asignadoExcl)) return false;
    if (!pasaFiltroChip(getTaskFilterCategories(t), categoriaIncl, categoriaExcl)) return false;
    const agenteTarea = t.agente_id_resuelto || t.agente_id || t.lead_agente_id;
    if (filtroUsuario !== "todos") {
      const agenteFiltro = agenteIdDeUsuarioFiltro(filtroUsuario);
      if (!agenteFiltro || String(agenteTarea) !== String(agenteFiltro)) return false;
    }
    if (filtroTipo !== "todos" && t.tipo !== filtroTipo) return false;
    if (!filtroFechaRangoMatch(filtroFecha, fechaReferenciaTarea(t), new Date(), fechaDesde, fechaHasta)) return false;
    const estado = String(t.estado || "").toLowerCase();
    if ((estado === "pendiente" || estado === "en_curso") && !isTareaVisibleEnTablero(t)) return false;
    return true;
  }), [
    tareas, busqueda, estadoModoConfig.excludeOverdue, asignadoIncl, asignadoExcl,
    categoriaIncl, categoriaExcl, filtroUsuario, filtroTipo, filtroFecha, fechaDesde, fechaHasta,
    usuariosFiltro,
  ]);

  const opcionesContacto = useMemo(() => {
    const map = new Map();
    for (const t of tareasTrasFiltrosBase) {
      const key = contactoKeyFromTarea(t);
      if (!key) continue;
      if (!map.has(key)) {
        const kind = key.split(":")[0];
        map.set(key, {
          key,
          label: contactoLabelFromTarea(t),
          kind,
        });
      }
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, "es", { sensitivity: "base" }));
  }, [tareasTrasFiltrosBase]);

  const opcionesContactoFiltradas = useMemo(() => {
    const q = contactoBusqueda.trim();
    if (!q) return opcionesContacto;
    return opcionesContacto.filter((o) => textIncludes(o.label, q));
  }, [opcionesContacto, contactoBusqueda]);

  const contactoSeleccionadoLabel = useMemo(() => {
    if (filtroContacto === "todos") return "Lead / contacto";
    const found = opcionesContacto.find((o) => o.key === filtroContacto);
    if (found) return found.label;
    return "Lead / contacto";
  }, [filtroContacto, opcionesContacto]);

  const tareasFiltradas = useMemo(() => {
    if (filtroContacto === "todos") return tareasTrasFiltrosBase;
    return tareasTrasFiltrosBase.filter((t) => contactoKeyFromTarea(t) === filtroContacto);
  }, [tareasTrasFiltrosBase, filtroContacto]);

  useEffect(() => {
    if (filtroContacto === "todos") return;
    if (!opcionesContacto.some((o) => o.key === filtroContacto)) {
      setFiltroContacto("todos");
    }
  }, [opcionesContacto, filtroContacto]);

  useEffect(() => {
    if (!contactoMenuOpen) return undefined;
    const syncPos = () => {
      const el = contactoFiltroRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setContactoMenuPos({
        top: r.bottom + 4,
        left: Math.min(r.left, window.innerWidth - 280),
        width: Math.max(r.width, 260),
      });
    };
    syncPos();
    const onDoc = (ev) => {
      if (contactoFiltroRef.current?.contains(ev.target)) return;
      if (ev.target?.closest?.("[data-tareas-contacto-menu]")) return;
      setContactoMenuOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === "Escape") setContactoMenuOpen(false);
    };
    window.addEventListener("resize", syncPos);
    window.addEventListener("scroll", syncPos, true);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => contactoBusquedaRef.current?.focus?.(), 30);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", syncPos);
      window.removeEventListener("scroll", syncPos, true);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [contactoMenuOpen]);

  const ordenarTareasLista = (lista) => [...lista].sort((a, b) => {
    if (estadoModo === "completadas" || estadoModo === "canceladas") {
      const ca = new Date(a.completada_en || a.updated_at || 0).getTime();
      const cb = new Date(b.completada_en || b.updated_at || 0).getTime();
      if (ca !== cb) return cb - ca;
    }
    const ta = a.fecha_vencimiento ? new Date(a.fecha_vencimiento).getTime() : Number.POSITIVE_INFINITY;
    const tb = b.fecha_vencimiento ? new Date(b.fecha_vencimiento).getTime() : Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
  });

  const tareasTablaFecha = modoTablaFecha ? ordenarTareasLista(tareasFiltradas) : [];
  /** Columna Agente solo si no hay filtro de un usuario concreto. */
  const mostrarColAgenteTabla = modoTablaFecha && filtroUsuario === "todos";

  // Columnas del pipeline con tareas (cada columna se oculta por completo si el
  // permiso granular t_col_* correspondiente no tiene "ver").
  const columnasData = COLUMNAS
    .filter((col) => puede(COLUMNA_NODE_ID[col.key], "ver"))
    .map(col => ({
      ...col,
      tareas: ordenarTareasLista(tareasFiltradas.filter(t => getColumna(t) === col.key)),
    }));

  const colAtrasadasDef = puede("t_col_atrasadas", "ver") ? COLUMNAS.find((c) => c.key === "atrasadas") : null;
  const conteoAtrasadas = columnasData.find((c) => c.key === "atrasadas")?.tareas.length ?? 0;
  const columnasVisibles = columnasData.filter((col) => atrasadasColExpandida || col.key !== "atrasadas");

  const totalActivas = tareas.filter((t) => {
    const e = String(t.estado || "").toLowerCase();
    return e === "pendiente" || e === "en_curso";
  }).length;
  const totalAtrasadas = tareasFiltradas.filter((t) => isTareaVencidaActiva(t)).length;
  const resumenEstado =
    estadoModo === "completadas"
      ? `${tareasFiltradas.length} completadas`
      : estadoModo === "canceladas"
        ? `${tareasFiltradas.length} canceladas`
        : estadoModo === "todas"
          ? `${tareasFiltradas.length} tareas`
          : estadoModo === "pendientes"
            ? `${tareasFiltradas.length} pendientes sin vencer`
            : `${totalActivas} activas${totalAtrasadas > 0 ? ` · ${totalAtrasadas} atrasadas` : ""}`;

  // ============================================================
  // ESTILOS BASE
  // ============================================================
  const inputStyle = {
    background: c.bgInput, border: `1px solid ${c.border}`,
    borderRadius: 8, padding: "7px 12px", color: c.textPrimary,
    fontSize: 16, outline: "none", fontFamily: "inherit",
  };

  const selectStyle = {
    ...inputStyle, cursor: "pointer", paddingRight: 30,
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
  };
  /** Selects del head de filtros: 2px más compactos para evitar scroll lateral. */
  const filtroSelectStyle = {
    ...selectStyle,
    fontSize: 14,
    padding: "5px 28px 5px 10px",
  };
  const dateInputStyle = {
    ...filtroSelectStyle,
    width: 146,
    minWidth: 146,
    paddingRight: 10,
    backgroundImage: "none",
    color: c.textPrimary,
    background: isDark ? c.bgPanel : "#fff",
    colorScheme: isDark ? "dark" : "light",
  };

  const abrirVistaPreviaRef = async (e, ref) => {
    e.stopPropagation();
    e.preventDefault();
    const referencia = String(ref || "").trim();
    if (!referencia) return;
    const propId = await resolverPropiedadIdPorReferencia(referencia);
    if (propId) {
      setModalPropRef(propId);
      return;
    }
    await dialogs.alert(`No se encontró la propiedad ${referencia}`);
  };

  const tarjetaLabelKey = { fontWeight: 700 };

  const abrirTareaDesdeUi = async (tarea) => {
    if (!tarea) return;
    setResultado("");
    const rawCode = String(parseTaskMetadata(tarea).task_code || "").trim().toUpperCase();
    // Visita ya terminada → M2 (aunque la tarjeta sea TC/T4 post-cualificación).
    if (taskIsM2Type(tarea)) {
      await abrirModalResultadoVisita(tarea);
      return;
    }
    // Cita en curso (TC): editar la visita en agenda, no cualificar aún.
    if (isVisitFeedbackTask(tarea)) {
      await abrirModalCitaVisita(tarea);
      return;
    }
    // M1 (T2a, T3b evaluar respuesta…): ficha + modal encima si hay cliente.
    if (taskIsM1Type(tarea)) {
      if (await abrirFichaConModalTarea(tarea)) return;
      let tareaFresh = tarea;
      if (tarea.lead_id) {
        try {
          await api.post(`/tareas/lead/${tarea.lead_id}/reabrir-modal`, { tarea_id: tarea.id });
          const r = await api.get(`/tareas/lead/${tarea.lead_id}/activa`);
          if (r.data?.tarea) tareaFresh = r.data.tarea;
        } catch { /* usa tarea original */ }
      }
      setModalCualificacion(tareaFresh);
      return;
    }
    if (isSinPropiedadesBuscarTask(tarea)) {
      setTareaDetalle(tarea);
      return;
    }
    // Post-T3: abrir ficha cliente (T3b con WA ya abre M1 vía taskIsM1Type arriba).
    if (isPostQualificationTaskCode(rawCode) && tarea.cliente_id) {
      navigate(`/clientes/${tarea.cliente_id}`, {
        state: taskNeedsM1Modal(tarea) || taskNeedsM2Modal(tarea) || isT4b1AutoContactTask(tarea)
          ? { abrirTareaVigente: true, tareaId: tarea.id }
          : undefined,
      });
      return;
    }
    if (tarea.lead_id) { await abrirLeadModal(tarea.lead_id); return; }
    if (isLeadTaskExpired(tarea)) {
      await dialogs.alert("El plazo de esta tarea ha vencido. Recarga la lista para ver el estado actual del lead.");
      return;
    }
    if (isAgenteResultTask(tarea) && !isWhatsappWaitingTask(tarea)) {
      setModalResultadoAgente(tarea);
      return;
    }
    if (isT4b1AutoContactTask(tarea)) return;
    if (tarea.lead_id && (isWhatsappWaitingTask(tarea) || isWhatsappFlowTask(tarea))) {
      const code = String(parseTaskMetadata(tarea).task_code || "").toUpperCase();
      if (code === "T4B1") return;
      const isVisitWa = ["T3B", "T3B1"].includes(code);
      const isT4bObjecionesDescarte = isT4bObjecionesDescarteTask(tarea);
      const ok = await dialogs.confirm(
        isWhatsappWaitingTask(tarea)
          ? (isT4bObjecionesDescarte
            ? "¿El cliente ha respondido? Se abrirá el resultado del contacto."
            : isVisitWa
              ? "¿El lead ha respondido al WhatsApp? Se abrirá el modal para agendar cita."
              : "¿El lead ha respondido al WhatsApp? Se abrirá la cualificación.")
          : "¿Has enviado el WhatsApp? Marca la respuesta del lead para continuar.",
      );
      if (!ok) return;
      try {
        await api.post(`/tareas/lead/${tarea.lead_id}/whatsapp-respondido`);
        await cargar();
        const r = await api.get(`/tareas/lead/${tarea.lead_id}/activa`);
        const tActualizada = r.data?.tarea || null;
        if (taskNeedsM1Modal(tActualizada)) {
          setModalCualificacion(tActualizada);
        } else if (taskIsM2Type(tActualizada)) {
          await abrirModalResultadoVisita(tActualizada);
        }
      } catch (err) {
        await dialogs.alert(err.response?.data?.error || "No se pudo registrar la respuesta");
      }
      return;
    }
    setTareaDetalle(tarea);
  };

  // ============================================================
  // TARJETA
  // ============================================================
  const TarjetaTarea = ({ tarea }) => {
    const tipo = TIPOS[tarea.tipo] || { label: tarea.tipo, emoji: "📋" };
    const waEnviada = isWhatsappWaitingTask(tarea);
    // El cliente ya respondió al WhatsApp de espera: la tarea deja de estar "en espera"
    // y pasa a requerir que el agente evalúe la respuesta (abre M1/M2). Lo reflejamos en
    // la tarjeta para que no parezca que sigue esperando.
    const replyPending = waEnviada && hasWhatsappReplyPending(tarea);
    const vis = getTarjetaTareaVisual(tarea, c, isDark);
    const vencida = vis.atrasada;
    const contactoEtiqueta = getTareaContactoEtiqueta(tarea);
    const accionLabel = formatTaskActionLabel(
      replyPending ? { ...tarea, titulo: "Evaluar respuesta" } : tarea,
      tipo.label,
    );
    const entradaStr = formatTareaCardFecha(getTareaEntradaIso(tarea));
    const vtoStr = formatTareaCardFecha(tarea.fecha_vencimiento);
    const propiedadRef = resolveTareaPropiedadRef(tarea);
    const chipBase = {
      fontWeight: 700,
      borderRadius: 4,
      padding: "2px 7px",
      fontSize: 12,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      flexShrink: 0,
    };
    const clienteChipStyle = {
      ...chipBase,
      background: c.accentGreen || "#22c55e",
      color: "#ffffff",
      border: `1px solid ${c.accentGreen || "#22c55e"}`,
      minWidth: 0,
      flexShrink: 1,
    };
    const agenteChipStyle = {
      ...chipBase,
      background: c.accentBlue || "#3b82f6",
      color: "#ffffff",
      border: `1px solid ${c.accentBlue || "#3b82f6"}`,
      minWidth: 0,
      flexShrink: 1,
    };
    const refChipStyle = {
      ...chipBase,
      background: "#f97316",
      color: "#ffffff",
      border: "1px solid #f97316",
      cursor: "pointer",
    };
    const tituloTarea = formatTaskTitleWithCode(
      replyPending ? { ...tarea, titulo: "Evaluar respuesta" } : tarea,
      tipo.label,
    );

    return (
      <div
        onClick={() => abrirTareaDesdeUi(tarea)}
        style={{
          background: vis.background,
          color: vis.textPrimary,
          borderTop: vis.borderTop,
          borderRight: vis.borderRight,
          borderBottom: vis.borderBottom,
          borderLeft: vis.borderLeft,
          boxShadow: vis.boxShadow,
          borderRadius: 9, padding: "11px 12px", cursor: "pointer",
          transition: "opacity 0.15s, filter 0.15s", marginBottom: 6,
          opacity: vis.opacity,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = vis.hoverBg;
          if (!vencida) e.currentTarget.style.filter = "brightness(0.94)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = vis.background;
          e.currentTarget.style.filter = "none";
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
          <IconoTareaCategoria
            categoryKey={vis.categoryKey}
            tipo={tipo}
            emojiBg={vis.emojiBg}
          />

          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Línea 1: Tipo de tarea */}
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: vis.textPrimary }}>
                {tituloTarea}
              </span>
              {waEnviada && !replyPending && (
                <span
                  title={buildTareaEnviadaTooltip(tarea)}
                  style={{
                    fontSize: 11, fontWeight: 700,
                    background: vencida ? "#db277722" : "#ec4899",
                    color: "#ffffff",
                    borderRadius: 3, padding: "1px 5px", flexShrink: 0,
                    letterSpacing: "0.04em",
                  }}
                >
                  ENVIADA
                </span>
              )}
              {replyPending && (
                <span
                  title="El cliente ha respondido — pendiente de evaluar"
                  style={{
                    fontSize: 11, fontWeight: 700,
                    background: vencida ? "#05966922" : "#10b981",
                    color: "#ffffff",
                    borderRadius: 3, padding: "1px 5px", flexShrink: 0,
                    letterSpacing: "0.04em",
                  }}
                >
                  RESPONDIÓ
                </span>
              )}
              {vencida && (
                <span style={{ fontSize: 11, fontWeight: 700, background: "#ef444422", color: "#ef4444", borderRadius: 3, padding: "1px 5px", flexShrink: 0 }}>
                  VENCIDA
                </span>
              )}
            </div>

            {/* Línea 2: Lead / cliente / captación */}
            {contactoEtiqueta && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 1, minWidth: 0, overflow: "hidden" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: vis.textSecondary, flexShrink: 0 }}>
                  {contactoEtiqueta.label}
                </span>
                <span style={clienteChipStyle}>
                  {contactoEtiqueta.nombre}
                </span>
              </div>
            )}

            {/* Línea 3: Acción */}
            <div style={{ fontSize: 13, color: vis.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 1 }}>
              <span style={tarjetaLabelKey}>Acción:</span> {accionLabel}
            </div>

            {/* Línea 4: Entrada y vencimiento */}
            {(entradaStr || vtoStr) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", marginTop: 4, fontSize: 12, color: vis.textMuted }}>
                {entradaStr && (
                  <span><span style={tarjetaLabelKey}>Entrada:</span> {entradaStr}</span>
                )}
                {vtoStr && (
                  <span style={{ color: vencida ? "#ef4444" : vis.textMuted, fontWeight: vencida ? 700 : 400 }}>
                    <span style={tarjetaLabelKey}>Vto.:</span> {vtoStr}
                  </span>
                )}
              </div>
            )}

            {/* Línea 5: Agente + referencia */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 6, fontSize: 12, minWidth: 0 }}>
              {tarea.agente_nombre ? (
                <span style={agenteChipStyle}>
                  {tarea.agente_nombre}
                </span>
              ) : (
                <span style={{ flex: 1 }} />
              )}
              {propiedadRef && (
                <span
                  role="link"
                  tabIndex={0}
                  title="Ver vista previa de la propiedad"
                  style={refChipStyle}
                  onClick={(e) => abrirVistaPreviaRef(e, propiedadRef)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") abrirVistaPreviaRef(e, propiedadRef);
                  }}
                >
                  Ref. {propiedadRef}
                </span>
              )}
            </div>
          </div>

          <div style={{
            width: 8, height: 8, borderRadius: "50%", background: vis.dotColor, flexShrink: 0, marginTop: 4,
            boxShadow: vis.atrasada ? "none" : "0 0 0 1px rgba(0,0,0,0.35)",
          }} />
        </div>
      </div>
    );
  };

  // ============================================================
  // MODAL DETALLE
  // ============================================================
  const ModalDetalle = ({ tarea, onClose }) => {
    if (!tarea) return null;
    const tipo = TIPOS[tarea.tipo] || { label: tarea.tipo, emoji: "📋" };
    const prioridad = PRIORIDADES[tarea.prioridad || "normal"];
    const esAna = isAnaTask(tarea);
    const vencida = getColumna(tarea) === "atrasadas";
    const esPendiente = tarea.estado === "pendiente" || tarea.estado === "en_curso";
    const tipoKey = String(tarea.tipo || "").trim().toLowerCase();
    const canalKey = String(tarea.canal || "").trim().toLowerCase();
    const contacto = getTaskContactInfo(tarea);
    const metadata = parseTaskMetadata(tarea);
    const quickOutcomes = getQuickOutcomeOptions(tarea);
    const needsVisitConfirm = taskNeedsM1Modal(tarea);
    const canCall = Boolean(contacto.telefonoLlamada) && (PHONE_TASK_TYPES.has(tipoKey) || canalKey === "llamada") && puede("t_detalle_llamar", "ver");
    const canWhatsApp = Boolean(contacto.telefonoWhatsApp) && (WHATSAPP_TASK_TYPES.has(tipoKey) || canalKey === "whatsapp") && puede("t_detalle_whatsapp", "ver");
    const canEmail = Boolean(contacto.email) && (EMAIL_TASK_TYPES.has(tipoKey) || canalKey === "email") && puede("t_detalle_email", "ver");
    const puedeVerResultadoRapido = puede("t_detalle_resultado_rapido", "ver");
    const puedeVerNotas = puede("t_detalle_notas", "ver");
    const puedeModificarNotas = puede("t_detalle_notas", "modificar");
    const puedeVerFichaDetalle = puede("t_detalle_ver_ficha", "ver");
    const puedeEliminarDetalle = puede("t_detalle_eliminar", "ver");
    const esManualUsuario = isTareaManualUsuario(tarea);
    const estadoTexto = getTaskEstadoTexto(tarea);
    const estadoStyle = getTaskEstadoStyle(tarea, theme, c);

    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }} onClick={onClose}>
        <div style={{
          background: c.bgPanel, borderRadius: 14, padding: 24,
          width: "100%", maxWidth: 500, maxHeight: "90vh",
          overflowY: "auto", overflowX: "hidden",
          border: `1px solid ${c.border}`,
          boxSizing: "border-box",
        }} onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
            <IconoTareaCategoria
              categoryKey={getTaskCategoryKey(tarea)}
              tipo={tipo}
              emojiBg={esAna ? "linear-gradient(135deg, #7c3aed, #a855f7)" : c.bgInput}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary, marginBottom: 6 }}>
                {formatTaskTitleWithCode(tarea, tipo.label)}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 600, background: `${prioridad.color}22`, color: prioridad.color, borderRadius: 4, padding: "2px 8px" }}>
                  {prioridad.label}
                </span>
                {esAna && <span style={{ fontSize: 11, fontWeight: 600, background: "#7c3aed22", color: "#a855f7", borderRadius: 4, padding: "2px 8px" }}>ANA IA</span>}
                {vencida && <span style={{ fontSize: 11, fontWeight: 600, background: "#ef444422", color: "#ef4444", borderRadius: 4, padding: "2px 8px" }}>VENCIDA</span>}
              </div>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", color: c.textMuted, cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 0 }}>×</button>
          </div>

          {/* Datos */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
            {tarea.descripcion && (
              <div style={{ background: c.bgInput, borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>DESCRIPCIÓN</div>
                <div style={{ fontSize: 13, color: c.textPrimary, lineHeight: 1.5 }}>{tarea.descripcion}</div>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {(tarea.lead_nombre || tarea.cliente_nombre || tarea.captacion_nombre) && (
                <div style={{ background: c.bgInput, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 2 }}>
                    {tarea.captacion_id && !tarea.lead_id ? "CAPTACIÓN" : "CLIENTE"}
                  </div>
                  <div style={{ fontSize: 13, color: c.textPrimary, fontWeight: 600 }}>
                    {(tarea.lead_nombre || tarea.cliente_nombre || tarea.captacion_nombre || "").trim()}
                  </div>
                </div>
              )}
              {contacto.telefonoLlamada && (
                <div style={{ background: c.bgInput, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 2 }}>TELÉFONO</div>
                  <CitaLinkTelefono
                    telefono={contacto.telefonoLlamada}
                    onLlamar={() => llamarTelefonoCRM(tarea, contacto.telefonoLlamada)}
                    color={c.accentBlue || "#3b82f6"}
                    style={{ fontSize: 13, fontWeight: 700 }}
                  />
                </div>
              )}
              {contacto.email && (
                <div style={{ background: c.bgInput, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 2 }}>EMAIL</div>
                  <a href={`mailto:${contacto.email}`} style={{ fontSize: 13, color: c.accentBlue || "#3b82f6", fontWeight: 600, textDecoration: "none" }}>
                    {contacto.email}
                  </a>
                </div>
              )}
              {tarea.propiedad_ref && (
                <div style={{ background: c.bgInput, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 2 }}>PROPIEDAD</div>
                  <div style={{ fontSize: 13, color: c.textPrimary, fontWeight: 600 }}>Ref {tarea.propiedad_ref}</div>
                </div>
              )}
              {tarea.agente_nombre && (
                <div style={{ background: c.bgInput, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 2 }}>AGENTE</div>
                  <div style={{ fontSize: 13, color: c.textPrimary }}>{tarea.agente_nombre}</div>
                </div>
              )}
              {tarea.canal && (
                <div style={{ background: c.bgInput, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 2 }}>CANAL</div>
                  <div style={{ fontSize: 13, color: c.textPrimary }}>{CANALES[tarea.canal] || ""} {tarea.canal}</div>
                </div>
              )}
              {tarea.fecha_inicio && (
                <div style={{ background: c.bgInput, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 2 }}>INICIO</div>
                  <div style={{ fontSize: 13, color: c.textPrimary, fontWeight: 600 }}>
                    {formatTaskFechaHoraES(tarea.fecha_inicio) ||
                      new Date(tarea.fecha_inicio).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}
                  </div>
                </div>
              )}
              {tarea.fecha_vencimiento && (
                <div style={{ background: vencida ? "#ef444411" : c.bgInput, borderRadius: 8, padding: "10px 14px", gridColumn: tarea.fecha_inicio ? undefined : "1 / -1" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: vencida ? "#ef4444" : c.textMuted, marginBottom: 2 }}>VENCIMIENTO</div>
                  <div style={{ fontSize: 13, color: vencida ? "#ef4444" : c.textPrimary, fontWeight: 600 }}>
                    {formatTaskFechaHoraES(tarea.fecha_vencimiento) ||
                      new Date(tarea.fecha_vencimiento).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}
                  </div>
                </div>
              )}
            </div>
            <div style={{
              background: estadoStyle.bg,
              border: estadoStyle.border,
              borderRadius: 8,
              padding: "10px 14px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: estadoStyle.labelColor, marginBottom: 4 }}>ESTADO</div>
              <div style={{ fontSize: 13, color: estadoStyle.textColor, lineHeight: 1.5 }}>{estadoTexto}</div>
            </div>
          </div>

          {/* Acciones */}
          {esPendiente ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {needsVisitConfirm && (
                <button
                  onClick={() => {
                    onClose();
                    setModalConfirmacionVisita(tarea);
                  }}
                  style={{
                    width: "100%", padding: "11px 14px", borderRadius: 8, border: "none",
                    background: "#7c3aed", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
                  }}
                >
                  Registrar resultado (confirmación visita)
                </button>
              )}
              {(canCall || canWhatsApp || canEmail) && (
                <div style={{ background: c.bgInput, borderRadius: 8, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: c.textMuted }}>
                    {canCall ? "ACCIÓN DE LLAMADA" : canWhatsApp ? "ACCIÓN DE WHATSAPP" : "ACCIÓN DE EMAIL"}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {canCall && (
                      <button
                        onClick={() => llamarTelefonoCRM(tarea, contacto.telefonoLlamada)}
                        style={{ padding: "9px 12px", borderRadius: 8, border: "none", background: "#16a34a", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                      >
                        📞 Llamar
                      </button>
                    )}
                    {canWhatsApp && (
                      <button
                        onClick={() => setModalWhatsAppTarea(tarea)}
                        style={{ padding: "9px 12px", borderRadius: 8, border: "none", background: "#16a34a", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                      >
                        💬 Enviar WhatsApp
                      </button>
                    )}
                    {canEmail && (
                      <button
                        onClick={() => setModalEmailTarea(tarea)}
                        style={{ padding: "9px 12px", borderRadius: 8, border: "none", background: "#d97706", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                      >
                        ✉️ Enviar Email
                      </button>
                    )}
                  </div>
                  {quickOutcomes.length > 0 && !needsVisitConfirm && puedeVerResultadoRapido && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {quickOutcomes.map((op) => (
                        <button
                          key={op.codigo}
                          onClick={() => completar(tarea, { resultado: op.resultado, resultadoCodigo: op.codigo })}
                          disabled={completando === tarea.id}
                          style={{ padding: "8px 11px", borderRadius: 999, border: `1px solid ${c.border}`, background: c.bgPanel, color: c.textSecondary, fontSize: 12, cursor: "pointer", opacity: completando === tarea.id ? 0.6 : 1 }}
                        >
                          {op.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {puedeVerNotas && (
                <textarea
                  value={resultado}
                  onChange={e => setResultado(e.target.value)}
                  placeholder="Notas (opcional)..."
                  rows={2}
                  disabled={!puedeModificarNotas}
                  title={!puedeModificarNotas ? "No tienes permiso para modificar las notas de la tarea" : undefined}
                  style={{ ...inputStyle, width: "100%", resize: "vertical", lineHeight: 1.5, boxSizing: "border-box", opacity: puedeModificarNotas ? 1 : 0.6, cursor: puedeModificarNotas ? "text" : "not-allowed" }}
                />
              )}
              <div style={{ display: "flex", gap: 8 }}>
                {esManualUsuario && esPendiente && (
                  <button
                    onClick={() => setTareaEditarManual(tarea)}
                    style={{
                      flex: 1,
                      padding: "9px 0",
                      borderRadius: 8,
                      border: "none",
                      background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Editar tarea
                  </button>
                )}
                {tarea.lead_id && puedeVerFichaDetalle && (
                  <button onClick={() => { onClose(); navigate(`/pipeline?openLead=${tarea.lead_id}`); }}
                    style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: `1px solid ${c.border}`, background: "none", color: c.textSecondary, fontSize: 13, cursor: "pointer" }}>
                    Ver ficha
                  </button>
                )}
                {puedeEliminarDetalle && (
                  <button onClick={() => eliminar(tarea)}
                    style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #ef444455", background: "#ef444411", color: "#ef4444", fontSize: 13, cursor: "pointer" }}
                    title="Eliminar permanentemente">
                    🗑
                  </button>
                )}
                <button onClick={onClose}
                  style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: `1px solid ${c.border}`, background: "none", color: c.textSecondary, fontSize: 13, cursor: "pointer" }}>
                  Cerrar
                </button>
              </div>
            </div>
          ) : (
            tarea.lead_id && puedeVerFichaDetalle && (
              <button onClick={() => { onClose(); navigate(`/pipeline?openLead=${tarea.lead_id}`); }}
                style={{ width: "100%", padding: "9px 0", borderRadius: 8, border: `1px solid ${c.border}`, background: "none", color: c.textSecondary, fontSize: 13, cursor: "pointer" }}>
                Ver ficha cliente
              </button>
            )
          )}
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={{ height: "calc(100vh - 49px)", display: "flex", flexDirection: "column", background: c.bgMain, overflow: "hidden" }}>

      {/* HEADER */}
      <div style={{ padding: "10px 22px", borderBottom: `1px solid ${c.border}`, background: c.bgPanel, flexShrink: 0 }}>

        {/* Fila 1 */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: c.textPrimary }}>✅ Tareas</div>
            <div style={{ fontSize: 11, color: c.textSecondary }}>
              {resumenEstado}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          {puede("t_buscador", "ver") && (
            <input
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              placeholder="🔍 Buscar por nombre, tipo, propiedad, agente..."
              style={{ ...inputStyle, width: 340 }}
            />
          )}
        </div>

        {/* Fila 2: Filtros — pills + selects en una sola línea (clic incluir, doble clic excluir) */}
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap", overflowX: "auto" }}>
          <div style={{ display: "flex", gap: 1, background: c.bgInput, borderRadius: 20, padding: 1, flexShrink: 0 }}>
            <TareasFiltroChip
              inGroup
              label="Activas"
              active={estadoModo === "activas"}
              excluded={false}
              onClick={() => onTareasFiltroChipPress("estado", "todos", true)}
              c={c}
              isDark={isDark}
            />
            {ESTADO_FILTRO_IDS.filter((id) => puede(ESTADO_FILTRO_NODE_ID[id], "ver")).map((id) => {
              const modoId = id === "pendiente" ? "pendientes" : id === "completada" ? "completadas" : id === "cancelada" ? "canceladas" : id;
              return (
              <TareasFiltroChip
                key={id}
                inGroup
                label={ESTADO_FILTRO_LABELS[id] || id}
                active={estadoModo === modoId}
                excluded={false}
                onClick={() => onTareasFiltroChipPress("estado", id)}
                c={c}
                isDark={isDark}
              />
              );
            })}
            <TareasFiltroChip
              inGroup
              label="Todas"
              active={estadoModo === "todas"}
              excluded={false}
              onClick={() => onTareasFiltroChipPress("estado", "todas")}
              c={c}
              isDark={isDark}
            />
          </div>

          <div style={{ display: "flex", gap: 1, background: c.bgInput, borderRadius: 20, padding: 1, flexShrink: 0 }}>
            <TareasFiltroChip
              inGroup
              label="Todos"
              active={!asignadoIncl.length && !asignadoExcl.length}
              excluded={false}
              onClick={() => onTareasFiltroChipPress("asignado", "todos", true)}
              c={c}
              isDark={isDark}
            />
            {puede("t_asig_agente", "ver") && (
              <TareasFiltroChip
                inGroup
                label="Agentes"
                active={asignadoIncl.includes("agente")}
                excluded={asignadoExcl.includes("agente")}
                onClick={() => onTareasFiltroChipPress("asignado", "agente")}
                c={c}
                isDark={isDark}
              />
            )}
            {puede("t_asig_ana", "ver") && (
              <TareasFiltroChip
                inGroup
                label="🤖 Ana"
                active={asignadoIncl.includes("ana")}
                excluded={asignadoExcl.includes("ana")}
                onClick={() => onTareasFiltroChipPress("asignado", "ana")}
                c={c}
                isDark={isDark}
              />
            )}
          </div>

          <div style={{ display: "flex", gap: 1, background: c.bgInput, borderRadius: 20, padding: 1, flexShrink: 0 }}>
            <TareasFiltroChip
              inGroup
              label="Todos"
              active={!categoriaIncl.length && !categoriaExcl.length}
              excluded={false}
              onClick={() => onTareasFiltroChipPress("categoria", "todos", true)}
              c={c}
              isDark={isDark}
            />
            {CATEGORIA_FILTRO_IDS.filter((id) => puede(CATEGORIA_FILTRO_NODE_ID[id], "ver")).map((id) => (
              <TareasFiltroChip
                key={id}
                inGroup
                label={CATEGORIA_FILTRO_LABELS[id] || id}
                active={categoriaIncl.includes(id)}
                excluded={categoriaExcl.includes(id)}
                onClick={() => onTareasFiltroChipPress("categoria", id)}
                c={c}
                isDark={isDark}
              />
            ))}
          </div>

          <div style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
            <div ref={contactoFiltroRef} style={{ position: "relative", flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => {
                  setContactoBusqueda("");
                  setContactoMenuOpen((o) => !o);
                }}
                title="Filtrar por lead o contacto"
                style={{
                  ...filtroSelectStyle,
                  minWidth: 156,
                  width: 168,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 6,
                  textAlign: "left",
                  paddingRight: filtroContacto !== "todos" ? 36 : 28,
                  color: filtroContacto === "todos" ? c.textSecondary : c.textPrimary,
                  fontWeight: filtroContacto === "todos" ? 400 : 600,
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {contactoSeleccionadoLabel}
                </span>
                <span style={{ flexShrink: 0, opacity: 0.7, fontSize: 10 }}>{contactoMenuOpen ? "▴" : "▾"}</span>
              </button>
              {filtroContacto !== "todos" && (
                <button
                  type="button"
                  title="Quitar filtro de contacto"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiltroContacto("todos");
                    setContactoMenuOpen(false);
                  }}
                  style={{
                    position: "absolute",
                    right: 24,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: isDark ? "#334155" : "#e2e8f0",
                    color: c.textPrimary,
                    cursor: "pointer",
                    fontSize: 12,
                    lineHeight: 1,
                    padding: "2px 5px",
                    borderRadius: 4,
                    zIndex: 1,
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {puede("t_filtro_agente_select", "ver") && (
              <select
                value={filtroUsuario}
                onChange={e => setFiltroUsuario(e.target.value)}
                disabled={soloCartera}
                title={soloCartera ? "Vista restringida a tu cartera" : undefined}
                style={{ ...filtroSelectStyle, minWidth: 146, width: 146, opacity: soloCartera ? 0.75 : 1 }}
              >
                <option value="todos">Todos los usuarios</option>
                {usuariosFiltro.map((u) => (
                  <option key={u.id} value={u.id}>{u.nombre}{u.agente_es_ia ? " ⚡" : ""}</option>
                ))}
              </select>
            )}

            <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ ...filtroSelectStyle, minWidth: 126, width: 126 }}>
              <option value="todos">Todos los tipos</option>
              {Object.entries(TIPOS).filter(([key]) => puede(TIPO_FILTRO_NODE_ID[key], "ver")).map(([key, val]) => (
                <option key={key} value={key}>{val.emoji} {val.label}</option>
              ))}
            </select>

            <select
              value={filtroFecha}
              onChange={(e) => {
                const v = e.target.value;
                setFiltroFecha(v);
                if (v === "custom") {
                  setTimeout(() => abrirPickerFecha(fechaDesdeRef.current), 50);
                } else {
                  setFechaDesde("");
                  setFechaHasta("");
                }
              }}
              style={{ ...filtroSelectStyle, minWidth: 146, width: 146 }}
              aria-label="Filtrar por fecha"
            >
              <option value="todos">Cualquier fecha</option>
              <option value="hoy">Hoy</option>
              <option value="esta_semana">Esta semana</option>
              <option value="este_mes">Este mes</option>
              <option value="semana_pasada">Semana pasada</option>
              <option value="mes_pasado">Mes pasado</option>
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="90d">Últimos 90 días</option>
              <option value="este_anio">Este año</option>
              <option value="anio_pasado">Año pasado</option>
              <option value="custom">Personalizado...</option>
            </select>
            {filtroFecha === "custom" && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: c.textMuted }}>
                  Desde
                  <input
                    ref={fechaDesdeRef}
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    onClick={(e) => abrirPickerFecha(e.currentTarget)}
                    style={dateInputStyle}
                    title="Fecha desde"
                  />
                </label>
                <span style={{ fontSize: 12, color: c.textMuted }}>—</span>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: c.textMuted }}>
                  Hasta
                  <input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    onClick={(e) => abrirPickerFecha(e.currentTarget)}
                    style={dateInputStyle}
                    title="Fecha hasta"
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contenido: tabla si hay filtro Fecha; si no, pipeline por columnas */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", padding: "16px 20px", minHeight: 0 }}>

        {modoTablaFecha ? (
          <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {cargando ? (
              <div style={{ textAlign: "center", paddingTop: 80, color: c.textMuted, fontSize: 13 }}>Cargando tareas...</div>
            ) : tareasTablaFecha.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: c.textMuted, fontSize: 13 }}>
                No hay tareas con los filtros aplicados
              </div>
            ) : (
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
                <table style={{
                  width: "100%",
                  tableLayout: "fixed",
                  borderCollapse: "collapse",
                }}>
                  <colgroup>
                    <col style={{ width: 36 }} />
                    <col style={{ width: mostrarColAgenteTabla ? "14%" : "16%" }} />
                    <col style={{ width: mostrarColAgenteTabla ? "12%" : "13%" }} />
                    {mostrarColAgenteTabla && <col style={{ width: "10%" }} />}
                    <col style={{ width: mostrarColAgenteTabla ? "10%" : "11%" }} />
                    <col style={{ width: mostrarColAgenteTabla ? "12%" : "14%" }} />
                    <col style={{ width: mostrarColAgenteTabla ? "10%" : "11%" }} />
                    <col style={{ width: mostrarColAgenteTabla ? "10%" : "11%" }} />
                    <col style={{ width: mostrarColAgenteTabla ? "11%" : "12%" }} />
                    <col style={{ width: mostrarColAgenteTabla ? "11%" : "12%" }} />
                  </colgroup>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${c.border}` }}>
                      <th style={{
                        padding: "8px 6px", background: isDark ? c.bgPanel : "#f1f5f9",
                        borderRight: `1px solid ${c.border}`, textAlign: "center",
                      }}>
                        <input
                          type="checkbox"
                          checked={tareasTablaFecha.length > 0 && tablaSelectedIds.size === tareasTablaFecha.length}
                          onChange={(e) => {
                            setTablaSelectedIds(
                              e.target.checked
                                ? new Set(tareasTablaFecha.map((t) => t.id))
                                : new Set()
                            );
                          }}
                          style={{ cursor: "pointer", width: 14, height: 14 }}
                          aria-label="Seleccionar todas"
                        />
                      </th>
                      {[
                        "Tipo",
                        "Lead / contacto",
                        ...(mostrarColAgenteTabla ? ["Agente"] : []),
                        "Teléfono",
                        "Mail",
                        "Fecha entrada",
                        "Fecha vencimiento",
                        "Ref.",
                        "Dirección",
                      ].map((label) => (
                        <th
                          key={label}
                          style={{
                            padding: "8px 8px",
                            textAlign: "left",
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                            color: c.textMuted,
                            background: isDark ? c.bgPanel : "#f1f5f9",
                            borderRight: `1px solid ${c.border}`,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tareasTablaFecha.map((tarea, rowIdx) => {
                      const tipo = TIPOS[tarea.tipo] || { label: tarea.tipo, emoji: "📋" };
                      const tituloTipo = formatTaskTitleWithCode(tarea, tipo.label);
                      const contacto = getTaskContactInfo(tarea);
                      const entradaStr = formatTareaCardFecha(getTareaEntradaIso(tarea)) || "—";
                      const vtoStr = formatTareaCardFecha(tarea.fecha_vencimiento) || "—";
                      const refs = resolveTareaPropiedadRefs(tarea);
                      const dirCap = direccionCaptacionTarea(tarea);
                      const isSelected = tablaSelectedIds.has(tarea.id);
                      const cat = estiloCategoriaTabla(tarea);
                      const avatarColor = colorAvatarContacto(contacto.nombre);
                      const vencida = isTareaVencidaActiva(tarea) || isTareaPlazoVencido(tarea);
                      const rowBaseBg = isSelected
                        ? (isDark ? "#1e3a5f" : "#eff6ff")
                        : rowIdx % 2 === 0
                          ? c.bgCard
                          : (isDark ? "rgba(148,163,184,0.06)" : "rgba(15,23,42,0.03)");
                      const rowTintBg = isSelected
                        ? rowBaseBg
                        : `linear-gradient(90deg, ${hexConAlpha(cat.border, isDark ? "28" : "18")} 0, ${hexConAlpha(cat.border, "00")} 72px), ${rowBaseBg}`;
                      const tdBase = {
                        padding: "8px 8px",
                        fontSize: 12,
                        color: c.textSecondary,
                        borderRight: `1px solid ${c.border}`,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "middle",
                      };
                      return (
                        <tr
                          key={tarea.id}
                          style={{
                            borderBottom: `1px solid ${c.border}`,
                            borderLeft: `4px solid ${cat.border}`,
                            background: rowTintBg,
                            cursor: "pointer",
                          }}
                          onClick={() => abrirTareaDesdeUi(tarea)}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = isDark
                                ? `linear-gradient(90deg, ${hexConAlpha(cat.border, "40")} 0, transparent 90px), ${c.bgCardHover || "#1e293b"}`
                                : `linear-gradient(90deg, ${hexConAlpha(cat.border, "30")} 0, transparent 90px), ${c.accentBlueLight || "#eff6ff"}`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = rowTintBg;
                          }}
                        >
                          <td
                            style={{ ...tdBase, textAlign: "center", padding: "8px 6px" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                const next = new Set(tablaSelectedIds);
                                if (e.target.checked) next.add(tarea.id);
                                else next.delete(tarea.id);
                                setTablaSelectedIds(next);
                              }}
                              style={{ cursor: "pointer", width: 14, height: 14 }}
                              aria-label="Seleccionar tarea"
                            />
                          </td>
                          <td style={{ ...tdBase, whiteSpace: "normal" }} title={tituloTipo}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                maxWidth: "100%",
                                padding: "3px 9px",
                                borderRadius: 999,
                                background: cat.key === "llamada_whatsapp"
                                  ? TASK_UI.dualLlamadaWhatsappBg
                                  : cat.solid,
                                color: cat.text,
                                fontWeight: 700,
                                fontSize: 11,
                                lineHeight: 1.25,
                                boxShadow: isDark ? "0 0 0 1px rgba(0,0,0,0.25)" : "0 1px 2px rgba(0,0,0,0.08)",
                              }}
                            >
                              <span aria-hidden style={{ flexShrink: 0 }}>{tipo.emoji}</span>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {tituloTipo}
                              </span>
                            </span>
                          </td>
                          <td style={{ ...tdBase, whiteSpace: "normal" }} title={contacto.nombre}>
                            {(() => {
                              const puedeAbrirContacto = Boolean(
                                tarea.cliente_id || tarea.captacion_id || tarea.lead_id
                              );
                              const tituloContacto = tarea.cliente_id
                                ? "Abrir ficha de cliente"
                                : tarea.captacion_id
                                  ? "Abrir ficha del propietario"
                                  : "Abrir ficha del lead";
                              const contenido = (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 7, maxWidth: "100%", minWidth: 0 }}>
                                  <span
                                    aria-hidden
                                    style={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: "50%",
                                      flexShrink: 0,
                                      background: avatarColor,
                                      color: "#fff",
                                      fontSize: 10,
                                      fontWeight: 800,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      letterSpacing: "-0.02em",
                                    }}
                                  >
                                    {contacto.iniciales}
                                  </span>
                                  <span style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    color: "#ffffff",
                                    fontWeight: 600,
                                    textDecoration: puedeAbrirContacto ? "underline" : "none",
                                    textUnderlineOffset: 2,
                                  }}>
                                    {contacto.nombre}
                                  </span>
                                </span>
                              );
                              if (!puedeAbrirContacto) return contenido;
                              return (
                                <button
                                  type="button"
                                  title={tituloContacto}
                                  onClick={(e) => abrirContactoDesdeTabla(e, tarea)}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  style={{
                                    display: "inline-flex",
                                    maxWidth: "100%",
                                    border: "none",
                                    background: "none",
                                    padding: 0,
                                    margin: 0,
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    textAlign: "left",
                                  }}
                                >
                                  {contenido}
                                </button>
                              );
                            })()}
                          </td>
                          {mostrarColAgenteTabla && (
                            <td
                              style={{ ...tdBase, whiteSpace: "normal" }}
                              title={tarea.agente_nombre || ""}
                            >
                              {tarea.agente_nombre ? (
                                <span style={{
                                  display: "inline-block",
                                  maxWidth: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  background: c.accentBlue || "#3b82f6",
                                  color: "#fff",
                                  fontWeight: 700,
                                  fontSize: 11,
                                }}>
                                  {tarea.agente_nombre}
                                </span>
                              ) : (
                                <span style={{ color: c.textMuted }}>—</span>
                              )}
                            </td>
                          )}
                          <td style={{ ...tdBase, color: contacto.telefonoLlamada ? c.textPrimary : c.textMuted }} title={contacto.telefonoLlamada || ""}>
                            {contacto.telefonoLlamada || "—"}
                          </td>
                          <td style={{ ...tdBase, color: contacto.email ? "#ffffff" : c.textMuted }} title={contacto.email || ""}>
                            {contacto.email || "—"}
                          </td>
                          <td style={tdBase} title={entradaStr}>{entradaStr}</td>
                          <td
                            style={{
                              ...tdBase,
                              color: vencida ? "#ef4444" : c.textSecondary,
                              fontWeight: vencida ? 700 : 400,
                            }}
                            title={vtoStr}
                          >
                            {vencida ? (
                              <span style={{
                                display: "inline-block",
                                padding: "2px 7px",
                                borderRadius: 6,
                                background: isDark ? "#ef444433" : "#fee2e2",
                                color: "#ef4444",
                                fontWeight: 700,
                              }}>
                                {vtoStr}
                              </span>
                            ) : vtoStr}
                          </td>
                          <td style={{ ...tdBase, whiteSpace: "normal" }} onClick={(e) => e.stopPropagation()}>
                            {refs.length === 0 ? (
                              <span style={{ color: c.textMuted }}>—</span>
                            ) : (
                              <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
                                {refs.map((ref) => (
                                  <button
                                    key={ref}
                                    type="button"
                                    title={`Vista previa ${ref}`}
                                    onClick={(e) => abrirVistaPreviaRef(e, ref)}
                                    style={{
                                      border: `1px solid ${isDark ? "#3b82f666" : "#93c5fd"}`,
                                      background: isDark ? "#1e3a5f" : "#eff6ff",
                                      padding: "1px 7px",
                                      margin: 0,
                                      borderRadius: 6,
                                      color: isDark ? "#93c5fd" : "#1d4ed8",
                                      fontWeight: 700,
                                      fontSize: 11,
                                      cursor: "pointer",
                                      fontFamily: "inherit",
                                    }}
                                  >
                                    {ref}
                                  </button>
                                ))}
                              </span>
                            )}
                          </td>
                          <td style={{ ...tdBase, whiteSpace: "normal" }} title={dirCap || ""}>
                            {dirCap ? (
                              <span style={{
                                display: "inline-block",
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                padding: "2px 7px",
                                borderRadius: 6,
                                background: isDark ? "#f9731633" : "#ffedd5",
                                color: isDark ? "#fdba74" : "#c2410c",
                                fontWeight: 600,
                                fontSize: 11,
                              }}>
                                {dirCap}
                              </span>
                            ) : (isCaptacionTask(tarea) ? "—" : "")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <>
            {!atrasadasColExpandida && colAtrasadasDef && (
              <button
                type="button"
                onClick={() => setAtrasadasColExpandida(true)}
                style={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 8,
                  padding: "9px 14px",
                  marginBottom: 12,
                  border: "none",
                  borderBottom: `2px solid ${colAtrasadasDef.color}`,
                  background: isDark ? `${colAtrasadasDef.color}14` : `${colAtrasadasDef.color}22`,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  width: "100%",
                  textAlign: "left",
                  borderRadius: 9,
                }}
              >
                <span style={{ fontSize: 14 }}>{colAtrasadasDef.emoji}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                  {colAtrasadasDef.label}
                </span>
                <span style={{
                  fontSize: 14, fontWeight: 800, color: "#fff",
                  background: colAtrasadasDef.color,
                  padding: "2px 10px", borderRadius: 20, minWidth: 24, textAlign: "center",
                }}>
                  {conteoAtrasadas}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: colAtrasadasDef.color }}>
                  Mostrar ▾
                </span>
              </button>
            )}

            <div style={{ flex: 1, overflowX: "auto", overflowY: "hidden", minHeight: 0 }}>
            {cargando ? (
              <div style={{ textAlign: "center", paddingTop: 80, color: c.textMuted, fontSize: 13 }}>Cargando tareas...</div>
            ) : (
              <div style={{ display: "flex", gap: 14, height: "100%", alignItems: "flex-start" }}>
                {columnasVisibles.map(col => (
                  <div key={col.key} style={{ width: 272, flexShrink: 0, display: "flex", flexDirection: "column", maxHeight: "100%" }}>

                    {/* Cabecera columna */}
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                      padding: "8px 12px", borderRadius: 9,
                      background: isDark ? `${col.color}18` : c.bgCard,
                      border: isDark ? `1px solid ${col.color}33` : `1px solid ${c.border}`,
                      borderLeft: `3px solid ${col.color}`,
                      flexShrink: 0,
                    }}>
                      <span style={{ fontSize: 15 }}>{col.emoji}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: isDark ? col.color : c.textPrimary }}>{col.label}</span>
                      {col.key === "atrasadas" && (
                        <button
                          type="button"
                          title="Ocultar columna Atrasadas"
                          onClick={(e) => { e.stopPropagation(); setAtrasadasColExpandida(false); }}
                          style={{
                            padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                            border: `1px solid ${col.color}55`, background: `${col.color}18`,
                            color: col.color, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                          }}
                        >
                          Ocultar ▴
                        </button>
                      )}
                      <span style={{
                        marginLeft: "auto", fontSize: 13, fontWeight: 700,
                        background: `${col.color}33`, color: isDark ? col.color : c.textPrimary,
                        borderRadius: 10, padding: "1px 8px", minWidth: 22, textAlign: "center",
                      }}>
                        {col.tareas.length}
                      </span>
                    </div>

                    {/* Tareas */}
                    <div style={{ overflowY: "auto", flex: 1, paddingRight: 2 }}>
                      {col.tareas.length === 0 ? (
                        <div style={{
                          textAlign: "center", padding: "24px 12px",
                          color: c.textMuted, fontSize: 14,
                          border: `1px dashed ${c.border}`, borderRadius: 9,
                        }}>
                          Sin tareas
                        </div>
                      ) : (
                        col.tareas.map(t => <TarjetaTarea key={t.id} tarea={t} />)
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
          </>
        )}
      </div>

      {contactoMenuOpen && contactoMenuPos && createPortal(
        <div
          data-tareas-contacto-menu
          style={{
            position: "fixed",
            top: contactoMenuPos.top,
            left: Math.max(8, contactoMenuPos.left),
            width: contactoMenuPos.width,
            zIndex: 1200,
            background: c.bgPanel,
            border: `1px solid ${c.border}`,
            borderRadius: 10,
            boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.45)" : "0 12px 28px rgba(15,23,42,0.18)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: 8, borderBottom: `1px solid ${c.border}` }}>
            <input
              ref={contactoBusquedaRef}
              value={contactoBusqueda}
              onChange={(e) => setContactoBusqueda(e.target.value)}
              placeholder="Buscar lead o contacto…"
              aria-label="Buscar lead o contacto"
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: c.bgInput,
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                padding: "7px 10px",
                color: c.textPrimary,
                fontSize: 13,
                outline: "none",
                fontFamily: "inherit",
              }}
            />
          </div>
          <div style={{ maxHeight: 280, overflowY: "auto", padding: 4 }}>
            <button
              type="button"
              onClick={() => {
                setFiltroContacto("todos");
                setContactoMenuOpen(false);
                setContactoBusqueda("");
              }}
              style={{
                width: "100%",
                textAlign: "left",
                border: "none",
                background: filtroContacto === "todos" ? (isDark ? "#1e3a5f" : "#eff6ff") : "transparent",
                color: c.textPrimary,
                padding: "8px 10px",
                borderRadius: 7,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: filtroContacto === "todos" ? 700 : 500,
              }}
            >
              Todos los leads / contactos
            </button>
            {opcionesContactoFiltradas.length === 0 ? (
              <div style={{ padding: "12px 10px", fontSize: 12, color: c.textMuted }}>
                Sin coincidencias con los filtros actuales
              </div>
            ) : (
              opcionesContactoFiltradas.map((o) => {
                const active = o.key === filtroContacto;
                const kindLabel = o.kind === "cliente" ? "Contacto" : o.kind === "lead" ? "Lead" : "Captación";
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => {
                      setFiltroContacto(o.key);
                      setContactoMenuOpen(false);
                      setContactoBusqueda("");
                    }}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      background: active ? (isDark ? "#1e3a5f" : "#eff6ff") : "transparent",
                      color: c.textPrimary,
                      padding: "8px 10px",
                      borderRadius: 7,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontSize: 13,
                      fontWeight: active ? 700 : 500,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {o.label}
                    </span>
                    <span style={{
                      flexShrink: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      color: o.kind === "cliente" ? "#22c55e" : o.kind === "lead" ? "#3b82f6" : "#f97316",
                      background: o.kind === "cliente"
                        ? (isDark ? "#22c55e22" : "#dcfce7")
                        : o.kind === "lead"
                          ? (isDark ? "#3b82f622" : "#dbeafe")
                          : (isDark ? "#f9731622" : "#ffedd5"),
                      padding: "2px 6px",
                      borderRadius: 999,
                    }}>
                      {kindLabel}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body,
      )}

      {/* MODAL */}
      {tareaDetalle && (
        <ModalDetalle
          tarea={tareaDetalle}
          onClose={() => { setTareaDetalle(null); setResultado(""); }}
        />
      )}
      {tareaEditarManual && (
        <ModalCrearTareaCaptacion
          isOpen
          tarea={tareaEditarManual}
          captacion={{
            id: tareaEditarManual.captacion_id,
            nombre: tareaEditarManual.captacion_nombre,
            telefono: tareaEditarManual.captacion_telefono,
            agente_id: tareaEditarManual.agente_id,
          }}
          onClose={() => setTareaEditarManual(null)}
          onGuardada={(updated) => {
            if (updated?.id) {
              setTareaDetalle((prev) => (prev && String(prev.id) === String(updated.id)
                ? { ...prev, ...updated }
                : prev));
            }
            setTareaEditarManual(null);
            cargar();
          }}
        />
      )}
      {modalWhatsAppTarea && (
        <ModalWhatsApp
          isOpen={true}
          contact={{
            nombre: getTaskContactInfo(modalWhatsAppTarea).nombre,
            telefono: getTaskContactInfo(modalWhatsAppTarea).telefonoWhatsApp,
            iniciales: getTaskContactInfo(modalWhatsAppTarea).iniciales,
          }}
          crmContext={{
            cliente_id: modalWhatsAppTarea.cliente_id || null,
            lead_id: modalWhatsAppTarea.lead_id || null,
          }}
          onClose={() => setModalWhatsAppTarea(null)}
          onSend={async () => {
            const tareaActual = modalWhatsAppTarea;
            setModalWhatsAppTarea(null);
            if (tareaActual?.lead_id && isWhatsappFlowTask(tareaActual) && !isSinPropiedadesBuscarTask(tareaActual)) {
              try {
                await api.post(`/tareas/lead/${tareaActual.lead_id}/whatsapp-enviado`);
              } catch {
                /* ignorar */
              }
              await cargar();
              return;
            }
            if (tareaActual && shouldAutoCompleteAfterExternalAction(tareaActual)) {
              await completar(tareaActual, { resultado: "WhatsApp enviado", resultadoCodigo: "whatsapp_enviado" });
            }
          }}
        />
      )}
      {modalEmailTarea && (
        <ModalEmail
          isOpen={true}
          contact={{
            nombre: getTaskContactInfo(modalEmailTarea).nombre,
            email: getTaskContactInfo(modalEmailTarea).email,
            iniciales: getTaskContactInfo(modalEmailTarea).iniciales,
          }}
          onClose={() => setModalEmailTarea(null)}
          onSend={async () => {
            const tareaActual = modalEmailTarea;
            setModalEmailTarea(null);
            if (tareaActual && shouldAutoCompleteAfterExternalAction(tareaActual)) {
              await completar(tareaActual, { resultado: "Email enviado", resultadoCodigo: "email_enviado" });
            }
          }}
        />
      )}
      {modalCualificacion?.lead_id && (
        <ModalResultadoCualificacion
          isOpen={true}
          onClose={() => setModalCualificacion(null)}
          tarea={modalCualificacion}
          lead={{
            id: modalCualificacion.lead_id,
            cliente_id: modalCualificacion.cliente_id || null,
            agente_id: modalCualificacion.agente_id || null,
            agente_nombre: modalCualificacion.agente_nombre || null,
            nombre: String(modalCualificacion.lead_nombre || "").trim().split(/\s+/)[0] || "",
            apellido: String(modalCualificacion.lead_nombre || "").trim().split(/\s+/).slice(1).join(" "),
            telefono: modalCualificacion.lead_telefono || modalCualificacion.lead_whatsapp || "",
            whatsapp: modalCualificacion.lead_whatsapp || modalCualificacion.lead_telefono || "",
            email: modalCualificacion.lead_email || "",
            propiedad_ref: modalCualificacion.propiedad_ref || "",
          }}
          theme={theme}
          onCompleted={async () => {
            setModalCualificacion(null);
            await cargar();
          }}
          onLlamarTelefono={() => llamarTelefonoCRM(modalCualificacion, getTaskContactInfo(modalCualificacion).telefonoLlamada)}
        />
      )}
      {modalResultadoVisita && (
        <ModalResultadoVisita
          isOpen={true}
          tarea={modalResultadoVisita}
          theme={theme}
          onClose={() => setModalResultadoVisita(null)}
          onCompleted={async () => {
            setModalResultadoVisita(null);
            await cargar();
          }}
          onSegundaVisita={async (tarea, ctx) => {
            setModalResultadoVisita(null);
            await abrirSegundaVisita(tarea, ctx);
          }}
        />
      )}
      {modalResultadoAgente && (
        <ModalResultadoTareaAgente
          isOpen={true}
          tarea={modalResultadoAgente}
          theme={theme}
          onClose={() => setModalResultadoAgente(null)}
          onCompleted={async () => {
            setModalResultadoAgente(null);
            await cargar();
          }}
        />
      )}
      {cargandoLead && !leadModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1400, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: c.bgPanel, color: c.textPrimary, padding: "12px 18px", borderRadius: 10, fontSize: 13, border: `1px solid ${c.border}` }}>
            Abriendo ficha…
          </div>
        </div>
      )}
      {leadModal && (
        <ModalNuevoLead
          isOpen={true}
          lead={leadModal}
          onClose={() => { setLeadModal(null); cargar(); }}
          onLeadUpdated={(actualizado) => {
            setLeadModal((prev) => (prev ? { ...prev, ...actualizado } : null));
          }}
        />
      )}
      {captacionModal && (
        <ModalNuevaPrecaptacion
          isOpen
          captacion={captacionModal}
          onClose={() => { setCaptacionModal(null); cargar(); }}
          onCreated={() => { setCaptacionModal(null); cargar(); }}
          onRemoteUpdate={(cap) => {
            setCaptacionModal((prev) => (prev?.id === cap?.id ? { ...prev, ...cap } : prev));
          }}
        />
      )}
      {modalPropRef && (
        <FichaResumenPropiedad propiedadId={modalPropRef} onClose={() => setModalPropRef(null)} />
      )}
      {modalCitaVisita && citaEdicionAgendaId && puede("t_modal_cita", "ver") && (
        <ModalCita
          isOpen={true}
          onClose={cerrarModalCitaVisita}
          onConfirm={handleCitaVisitaConfirm}
          modoEdicion={true}
          readOnly={!puede("t_modal_cita", "modificar")}
          initialValues={citaEdicionInitial}
          contact={getTaskContactInfo(modalCitaVisita)}
          properties={citaEdicionProperties}
          agentName={modalCitaVisita.agente_nombre || ""}
          onLlamarTelefono={(tel) => llamarTelefonoCRM(modalCitaVisita, tel)}
          onAbrirVistaPrevia={(propId) => setModalPropRef(propId)}
          onAbrirFichaCliente={(clienteId) => { if (clienteId) transferCitaVisitaModalAFicha(`/clientes/${clienteId}`); }}
          onAbrirFichaContacto={(contactoId) => { if (contactoId) transferCitaVisitaModalAFicha(`/contactos/${contactoId}`); }}
          onBuscarPropiedadPorRef={(ref) => fetchPropiedadPorRef(api, ref)}
          footerExtra={
            puede("t_modal_cita", "modificar") ? (
              <button
                type="button"
                onClick={handleCancelarCitaVisita}
                style={{
                  padding: "11px 18px", borderRadius: 10,
                  border: "1.5px solid #EF4444", background: "#EF444422", color: "#EF4444",
                  cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600,
                }}
              >
                Cancelar cita
              </button>
            ) : null
          }
        />
      )}
      {segundaVisitaTarea && (
        <ModalCita
          isOpen={true}
          onClose={cerrarSegundaVisita}
          onConfirm={handleSegundaVisitaConfirm}
          modoEdicion={false}
          cerrarAlClicFuera={false}
          contact={getTaskContactInfo(segundaVisitaTarea)}
          properties={segundaVisitaProps}
          agentName={segundaVisitaTarea.agente_nombre || ""}
          onLlamarTelefono={(tel) => llamarTelefonoCRM(segundaVisitaTarea, tel)}
          onAbrirVistaPrevia={(propId) => setModalPropRef(propId)}
          onAbrirFichaCliente={(clienteId) => { if (clienteId) navigate(`/clientes/${clienteId}`); }}
          onAbrirFichaContacto={(contactoId) => { if (contactoId) navigate(`/contactos/${contactoId}`); }}
          onBuscarPropiedadPorRef={(ref) => fetchPropiedadPorRef(api, ref)}
        />
      )}
    </div>
  );
}
